import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { RentaEmailConfigButton } from '../../components/analista/RentaEmail';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Pencil, 
  Plus, 
  Home,
  FileText,
  Settings,
  Check,
  AlertCircle,
  AlertTriangle,
  Upload,
  Trash2,
  ListPlus,
  Save,
  ChevronLeft,
  ChevronRight,
  Zap,
  User,
  Users,
  UserPlus,
  KeyRound,
  Download,
  File,
  X,
  Sparkles,
  Loader2,
  Wallet,
  Search,
  Receipt,
  DollarSign,
  RefreshCw,
  Layers,
  ChevronDown,
  ChevronUp,
  Edit,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import { edificiosApi, unidadesApi, resultadosApi, propietariosApi, archivosApi, cotizacionesApi, facturacionApi, gruposUnidadesApi, contratosApi, historialApi, colppyApi, iaApi, reordenarApi, expensasApi } from '../../lib/api';
import { ContratosDepositos } from '../../components/analista/ContratosDepositos';
import { formatCurrency, formatPeriodo, getStatusLabel, getFacturacionPeriods, cn, formatDateDMY, parseDateDMY, formatImporte } from '../../lib/utils';
import { StatusBadge } from '../../components/common/StatusBadge';
import { PeriodSelector } from '../../components/common/PeriodSelector';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
// Tab components
import { TabFactura } from './tabs/TabFactura';
import { TabCobro } from './tabs/TabCobro';
import { TabMorosos } from './tabs/TabMorosos';
import { TabEgresos } from './tabs/TabEgresos';
import { TabPagos } from './tabs/TabPagos';
import { TabLiquidacion } from './tabs/TabLiquidacion';
import { TabVistaInversor } from './tabs/TabVistaInversor';
import { TabUnidades } from './tabs/TabUnidades';
import { TabHistoricos } from './tabs/TabHistoricos';
import { TabInquilinos } from './tabs/TabInquilinos';
import { TabAdministracion } from './tabs/TabAdministracion';
import { TabFacturaPropietarioForesta } from './tabs/TabFacturaPropietarioForesta';
import { TabComprobanteInquilinoForesta } from './tabs/TabComprobanteInquilinoForesta';
import { TabPendientesForesta } from './tabs/TabPendientesForesta';
import { TabControlMensualForesta } from './tabs/TabControlMensualForesta';
import CoworkForesta from './CoworkForesta';
import { TabForestaStub } from './tabs/TabForestaStub';
import { TabAlquilerMensualForesta } from './tabs/TabAlquilerMensualForesta';
import { TabMorososForesta } from './tabs/TabMorososForesta';
// (TabExpensasForesta deprecado dentro del módulo del edificio Foresta — las
// expensas viven en el módulo Expensas separado. El componente sigue exportado
// por si otro lugar lo usa, pero ya no se monta acá.)
// import { TabExpensasForesta } from './tabs/TabExpensasForesta';
import { TabCobrosForesta } from './tabs/TabCobrosForesta';
import { useAuth } from '../../contexts/AuthContext';
// Dialog components
import { EditEdificioDialog } from './dialogs/EditEdificioDialog';
import { UnidadDialog } from './dialogs/UnidadDialog';
import { PropietarioDialog } from './dialogs/PropietarioDialog';
import { EscrituraDialog } from './dialogs/EscrituraDialog';
import { CartaOfertaDialog } from './dialogs/CartaOfertaDialog';
import { PosesionDialog } from './dialogs/PosesionDialog';
import { NuevoPropietarioDialog } from './dialogs/NuevoPropietarioDialog';
import { MasivoDialog } from './dialogs/MasivoDialog';
import { AltaRapidaDialog } from './dialogs/AltaRapidaDialog';
import { GrupoDialog } from './dialogs/GrupoDialog';
import { AltaAdminDialog } from './dialogs/AltaAdminDialog';
import { EditAdminDialog } from './dialogs/EditAdminDialog';
import { BajaAdminDialog } from './dialogs/BajaAdminDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

// Componente para editar fecha de cobro con formato dd/mm/aaaa
const FechaCobro = ({ initialValue, onSave }) => {
  const [displayValue, setDisplayValue] = React.useState('');
  
  // Convertir YYYY-MM-DD a DD/MM/YYYY
  const formatToDisplay = (isoDate) => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDate;
  };
  
  // Inicializar valor cuando cambia initialValue
  React.useEffect(() => {
    setDisplayValue(formatToDisplay(initialValue));
  }, [initialValue]);
  
  const handleChange = (e) => {
    let input = e.target.value;
    
    // Solo permitir números y /
    input = input.replace(/[^\d/]/g, '');
    
    // Obtener solo los números
    const numbersOnly = input.replace(/\//g, '');
    
    // Formatear automáticamente con barras
    let formatted = '';
    for (let i = 0; i < numbersOnly.length && i < 8; i++) {
      formatted += numbersOnly[i];
      // Agregar barra después del día (posición 2) y mes (posición 4)
      if ((i === 1 || i === 3) && i < numbersOnly.length - 1) {
        formatted += '/';
      }
    }
    // Si escribió exactamente 2 o 4 números, agregar la barra al final
    if (numbersOnly.length === 2 || numbersOnly.length === 4) {
      formatted += '/';
    }
    
    setDisplayValue(formatted);
  };
  
  const handleBlur = async () => {
    // Si tiene formato completo dd/mm/yyyy, guardar
    if (displayValue.length === 10 && displayValue.split('/').length === 3) {
      const parts = displayValue.split('/');
      if (parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        
        // Validar fecha
        const dateObj = new Date(`${year}-${month}-${day}`);
        if (!isNaN(dateObj.getTime())) {
          const isoDate = `${year}-${month}-${day}`;
          if (isoDate !== initialValue) {
            await onSave(isoDate);
          }
          return;
        }
      }
    }
    
    if (displayValue === '' && initialValue) {
      // Si se borró, limpiar la fecha
      await onSave('');
    } else if (displayValue !== '' && displayValue !== formatToDisplay(initialValue)) {
      // Restaurar valor original si está incompleto o inválido
      setDisplayValue(formatToDisplay(initialValue));
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };
  
  return (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="dd/mm/aaaa"
      className="h-7 text-xs w-[105px] text-center"
    />
  );
};

const fileTypes = {
  detalle_costo_servicio: 'Detalle Costo',
  determinacion_ganancia: 'Det. Ganancia',
  factura_alquiler: 'Factura',
};

const initialUnidadForm = {
  nombre_unidad: '',
  tipo: 'oficina',
  m2_cubiertos: '',
  m2_semicubiertos: '',
  m2_comunes: '',
  m2_descubiertos: '',
  m2_balcon: '',
  m2_totales: '',
  m2_alquiler: '',
  m2_expensas: '',
  m2_cubiertos_total: '',
  m2_cochera: '',
  nomenclatura_catastral: '',
  padron_territorial: '',
  nic_energia_electrica: '',
  nic_energia_no_aplica: false,
  nic_agua: '',
  nic_agua_no_aplica: false,
  tasa_municipal: '',
  tasa_municipal_no_aplica: false,
  tasa_comercial: '',
  tasa_comercial_no_aplica: false,
  // Foresta-only: Cochera
  tiene_cochera: false,
  cochera_numero: '',
  cochera_observaciones: '',
  // Foresta-only: Amoblamiento
  tiene_amoblamiento: false,
  amoblamiento_detalle: {
    aires: false,
    cortinas: false,
    muebles: false,
    sillas: false,
    pizarra: false,
  },
  // Inversión / Compra (solapa Inversión)
  fecha_adquisicion: '',
  monto_inversion: { monto: null, moneda: 'USD', iva: null },
  // Capacidad (solapa Capacidad)
  capacidad_personas: '',
  escritorios_cantidad: '',
  sillas_cantidad: '',
  descripcion_equipamiento: '',
};

const initialPropietarioForm = {
  propietario_id: '',
  fecha_adquisicion: '',
  monto_inversion: { monto: '', moneda: 'USD' },
  // Nuevo: múltiples propietarios con porcentaje
  propietarios: [], // [{propietario_id, porcentaje, fecha_adquisicion, monto_inversion}]
};

const initialAlquilerForm = {
  en_administracion: false,
  estado_alquiler: 'vacante',
  tipo_administracion: 'full',
  porcentaje_administracion: 20, // Default 20% para full
  inquilino: '',
  fecha_inicio: '',
  fecha_fin: '',
  monto_alquiler_inicio: { monto: '', moneda: 'ARS' },
  alquiler_actual: { monto: '', moneda: 'ARS' },
  indice_actualizacion: { tipo: 'ipc', valor: '', frecuencia: 'trimestral' },
};

export const AnalistaEdificioDetalle = () => {
  const { edificioId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isComercial = user?.rol === 'comercial';
  // Mismo criterio que el guard de ruta /analista: rol primario O roles_extra.
  const isAnalista = [user?.rol, ...(user?.roles_extra || [])].filter(Boolean).includes('analista');
  const [editAdminUnidad, setEditAdminUnidad] = useState(null);
  
  const [edificio, setEdificio] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [resultadosCargados, setResultadosCargados] = useState(false);
  const [propietarios, setPropietarios] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Derivado: mapa unidad_id → info de contrato multi-unidad vigente ──
  // Genera el label para el badge "Incluida en contrato {inquilino}" en Tabs 8/9/10.
  const contratoBadgeByUnidad = React.useMemo(() => {
    const out = {};
    const tipoById = {};
    for (const u of unidades || []) tipoById[u.unidad_id] = u.tipo;
    // Resuelve la unidad principal a la oficina/local/cowork, nunca a una cochera
    // (mismo criterio que el backend de 5.Factura/8.Egresos: oficina > cochera).
    const resolverPrincipal = (c) => {
      const cand = c.unidad_principal_comercial_id || c.unidad_id;
      if (tipoById[cand] !== 'cochera') return cand;
      const uc = c.unidades_contrato || [];
      for (const pref of ['oficina', 'local', 'cowork']) {
        const found = uc.find(x => tipoById[x.unidad_id] === pref);
        if (found) return found.unidad_id;
      }
      return cand;
    };
    for (const c of contratos || []) {
      if (c.estado !== 'vigente') continue;
      const uc = c.unidades_contrato || [];
      if (uc.length <= 1) continue;
      const inquilino = c.nombre_comercial || c.razon_social || c.inquilino || 'inquilino';
      const principalId = resolverPrincipal(c);
      for (const u of uc) {
        const esPrincipal = u.unidad_id === principalId;
        out[u.unidad_id] = {
          contrato_id: c.contrato_id,
          inquilino,
          es_principal: esPrincipal,
          tipo: esPrincipal ? 'principal' : 'secundaria',
          texto: esPrincipal
            ? `Principal ${inquilino}`
            : `Incluida ${inquilino}`,
        };
      }
    }
    return out;
  }, [contratos, unidades]);

  // Tab activa - se inicializa desde la URL o usa 'carga' por defecto (para comercial: 'administracion')
  // Redirige tab=detalle (legacy, ya eliminada) a tab=egresos para no romper enlaces existentes.
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = searchParams.get('tab');
    if (fromUrl === 'detalle') return 'egresos';
    if (fromUrl) return fromUrl;
    return isComercial ? 'administracion' : 'carga';
  });

  // Mide el alto del header sticky para que el área de contenido ocupe exactamente
  // el resto del viewport (sin franja blanca y recalculando ante cualquier cambio
  // de header/KPIs/toolbar/tamaño de pantalla). General para todos los módulos.
  const stickyHeaderRef = useRef(null);
  const [stickyH, setStickyH] = useState(0);
  useLayoutEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const update = () => setStickyH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [loading, activeTab, isComercial]);
  
  // Actualizar URL cuando cambia la pestaña
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams(prev => ({ tab: newTab, periodo: prev.get('periodo') || selectedPeriodo }));
  };

  // Redirige tab=cobro (legacy "7. Expensas" dentro del edificio Foresta, ya eliminada)
  // a tab=cobros, sólo para Foresta. En TE 'cobro' sigue siendo una tab válida.
  // Usamos el patrón "prev state diff" para evitar la regla react-hooks/set-state-in-effect.
  const redirectKey = `${activeTab}|${edificio?.modelo_administracion || ''}`;
  const [_redirectPrev, _setRedirectPrev] = useState(redirectKey);
  if (_redirectPrev !== redirectKey) {
    _setRedirectPrev(redirectKey);
    if (
      activeTab === 'cobro' &&
      edificio?.modelo_administracion === 'propietario_factura_directo'
    ) {
      // Disparamos el cambio en el siguiente tick para no anidar setState dentro del render path.
      Promise.resolve().then(() => handleTabChange('cobros'));
    }
  }
  
  // Period selection - se inicializa desde la URL o usa el mes actual
  const [selectedPeriodo, setSelectedPeriodo] = useState(() => {
    const fromUrl = searchParams.get('periodo');
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) return fromUrl;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Sincronizar período con la URL
  useEffect(() => {
    setSearchParams(prev => ({ tab: prev.get('tab') || activeTab, periodo: selectedPeriodo }));
  }, [selectedPeriodo]);
  
  // Table data for batch editing
  const [tableData, setTableData] = useState({});
  const [savingBatch, setSavingBatch] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const autoSaveTimers = useRef({});
  
  // Expensas: mapa unidad_id → total positivo (solo si la liquidación está aprobada).
  // Se usa para incluir el monto de expensas en calcEgresoFromState (igual que TabEgresos).
  const [expensasLiqMap, setExpensasLiqMap] = useState({});
  const [expensasLiqEstado, setExpensasLiqEstado] = useState(null);
  
  // Estado para rastrear facturas creadas en Colppy (por unidad_id + periodo)
  const [facturasCreadas, setFacturasCreadas] = useState({});
  
  // Cotización del período para cálculo de rentabilidad
  const [cotizacionPeriodo, setCotizacionPeriodo] = useState(null);
  
  // File upload state
  const [uploadingFile, setUploadingFile] = useState(null);
  
  // Edit edificio
  const [isEditEdificioOpen, setIsEditEdificioOpen] = useState(false);
  const [edificioForm, setEdificioForm] = useState({ nombre: '', denominacion: '', direccion: '', descripcion: '', estado: 'activo', valor_cochera_estandar: null });
  
  // Unidades
  const [isUnidadDialogOpen, setIsUnidadDialogOpen] = useState(false);
  const [isMasivoDialogOpen, setIsMasivoDialogOpen] = useState(false);
  const [isAltaRapidaDialogOpen, setIsAltaRapidaDialogOpen] = useState(false);
  const [isPropietarioDialogOpen, setIsPropietarioDialogOpen] = useState(false);
  const [isAlquilerDialogOpen, setIsAlquilerDialogOpen] = useState(false);
  const [isDeleteUnidadOpen, setIsDeleteUnidadOpen] = useState(false);
  const [deleteGrupoTarget, setDeleteGrupoTarget] = useState(null);
  const [bajaAdminTarget, setBajaAdminTarget] = useState(null);
  const [selectedUnidad, setSelectedUnidad] = useState(null);
  const [unidadForm, setUnidadForm] = useState(initialUnidadForm);
  const [unidadTab, setUnidadTab] = useState('general'); // 'general', 'servicios' o 'ia'
  
  // Estado para análisis de escritura con IA
  const [escrituraFile, setEscrituraFile] = useState(null);
  const [analizandoEscritura, setAnalizandoEscritura] = useState(false);
  const [escrituraAnalisis, setEscrituraAnalisis] = useState(null);
  const [isEscrituraDialogOpen, setIsEscrituraDialogOpen] = useState(false);
  const [isCartaOfertaDialogOpen, setIsCartaOfertaDialogOpen] = useState(false);
  const [selectedUnidadCarta, setSelectedUnidadCarta] = useState(null);
  const [isPosesionDialogOpen, setIsPosesionDialogOpen] = useState(false);
  const [selectedUnidadPosesion, setSelectedUnidadPosesion] = useState(null);
  const [selectedUnidadEscritura, setSelectedUnidadEscritura] = useState(null);
  const [propietarioForm, setPropietarioForm] = useState(initialPropietarioForm);
  const [propietarioSearch, setPropietarioSearch] = useState(''); // Buscador de propietarios
  const [isNuevoPropietarioOpen, setIsNuevoPropietarioOpen] = useState(false); // Modal nuevo propietario
  const [showNuevoPropPassword, setShowNuevoPropPassword] = useState(false); // Mostrar/ocultar contraseña
  const [nuevoPropietarioTab, setNuevoPropietarioTab] = useState('titular'); // Pestaña activa
  const [nuevoPropietarioForm, setNuevoPropietarioForm] = useState({
    // ─── Titular ───
    tipo: 'persona_humana',
    nombre: '',
    apellido: '',
    razon_social: '',
    nombre_razon_social: '',  // legacy/derivado
    dni: '',
    email_legal: '',
    direccion: '',
    notas: '',
    // ─── Contacto ───
    contact_is_same_as_owner: true,
    contact_person_first_name: '',
    contact_person_last_name: '',
    email: '',
    whatsapp: '',
    preferencia_contacto: 'email',
    // ─── Fiscal ───
    razon_social_fiscal: '',
    cuit_cuil: '',
    condicion_iva: '',
    domicilio_fiscal: '',
    email_facturacion: '',
    tipo_factura: '',
    // ─── Bancario ───
    cbu: '',
    alias_cbu: '',
    banco: '',
    titular_cuenta: '',
    // Legacy (no usado en form nuevo pero mantenido por compatibilidad)
    telefono: '',
    localidad: '',
    provincia: '',
    codigo_postal: '',
  });
  const [alquilerForm, setAlquilerForm] = useState(initialAlquilerForm);
  const [masivoData, setMasivoData] = useState({ propietario_id: '', tipo: 'oficina', nombres: '' });
  const [altaRapidaData, setAltaRapidaData] = useState([{ nombre_unidad: '', tipo: 'oficina' }]);
  const [contratoFile, setContratoFile] = useState(null);
  const [uploadingContrato, setUploadingContrato] = useState(false);
  const [analizandoContrato, setAnalizandoContrato] = useState(false);
  
  // Grupos de unidades
  const [grupos, setGrupos] = useState([]);
  const [isGrupoDialogOpen, setIsGrupoDialogOpen] = useState(false);
  const [grupoForm, setGrupoForm] = useState({ nombre: '', unidad_ids: [], mismo_propietario: false });
  const [expandedGrupos, setExpandedGrupos] = useState({});
  const [savingGrupo, setSavingGrupo] = useState(false);
  const [editingGrupoId, setEditingGrupoId] = useState(null); // null = crear, string = editar
  
  // Alta Administración → ahora "Alta de Servicios" (búsqueda + admin mensual, independientes)
  const [isAltaAdminOpen, setIsAltaAdminOpen] = useState(false);
  const [altaAdminForm, setAltaAdminForm] = useState({
    unidad_id: '',
    // Servicios independientes
    servicio_busqueda_activo: false,
    servicio_admin_activo: false,
    // Admin mensual
    tipo_administracion: 'full',
    porcentaje_administracion: 20,
    contrato_file: null,
    contrato_tipo: 'mismo',
    alcance_grupo: 'solo_unidad',
    // Búsqueda de inquilino
    contrato_busqueda_file: null,
  });
  const [savingAltaAdmin, setSavingAltaAdmin] = useState(false);
  const [uploadingContratoAdmin, setUploadingContratoAdmin] = useState(false);
  const [uploadingContratoBusqueda, setUploadingContratoBusqueda] = useState(false);
  
  // Filtros para Info Oficina
  const [filtroTipo, setFiltroTipo] = useState('todas'); // todas, oficina, cowork, local, cochera
  const [busquedaOficina, setBusquedaOficina] = useState('');
  
  const [saving, setSaving] = useState(false);

  // Estado para facturación/cobro
  const [facturacionData, setFacturacionData] = useState(null);
  const [loadingFacturacion, setLoadingFacturacion] = useState(false);
  const [filtroEstadoFactura, setFiltroEstadoFactura] = useState('todos');
  const [indicadoresFacturacion, setIndicadoresFacturacion] = useState(null);
  const [morososData, setMorososData] = useState(null);
  const [loadingMorosos, setLoadingMorosos] = useState(false);

  // Estado para sincronización con Colppy
  const [syncingColppy, setSyncingColppy] = useState(false);
  
  // Estado para modo edición de propietario
  const [editingPropietario, setEditingPropietario] = useState(false);

  // Estado para egresos
  const [egresosData, setEgresosData] = useState({});
  const [savingEgresos, setSavingEgresos] = useState(false);
  const [egresosHasChanges, setEgresosHasChanges] = useState(false);

  // Estado para alquileres precargados (cálculo automático desde contratos)
  const [alquileresPrecargados, setAlquileresPrecargados] = useState({});
  // Monto neto FULL de 5.Factura por unidad principal facturable (fuente de verdad de ingresos).
  const [montoNetoByUnidad, setMontoNetoByUnidad] = useState({});
  // Σ cocheras externas descontadas del contrato, por oficina madre (se rinden a su dueño).
  const [externasByMadre, setExternasByMadre] = useState({});
  const [cocherasAsociadasMap, setCocherasAsociadasMap] = useState({});  // cochera_id → oficina_madre_id
  const [cocherasDetalleByMadre, setCocherasDetalleByMadre] = useState({});  // madre_id → [{ unidad_id, nombre, ingreso, egreso }]
  const [cargandoAlquileres, setCargandoAlquileres] = useState(false);

  // Estado para históricos
  const [historicosData, setHistoricosData] = useState([]);
  const [loadingHistoricos, setLoadingHistoricos] = useState(false);
  const [expandedHistoricos, setExpandedHistoricos] = useState({});

  // Estado para cambiar propietario
  const [isCambiarPropietarioOpen, setIsCambiarPropietarioOpen] = useState(false);
  const [cambiarPropietarioForm, setCambiarPropietarioForm] = useState({
    propietario_id: '',
    fecha_adquisicion: '',
    monto_inversion: { monto: '', moneda: 'USD' }
  });

  useEffect(() => {
    fetchData();
  }, [edificioId]);

  useEffect(() => {
    // Build table data when periodo, resultados, or egresos change
    buildTableData();
  }, [selectedPeriodo, resultados, unidades, cotizacionPeriodo, alquileresPrecargados, montoNetoByUnidad, externasByMadre, egresosData, expensasLiqMap, expensasLiqEstado, facturacionData, contratos]);

  // Auto-save units: new records only
  const autoSavedPeriods = useRef(new Set());
  useEffect(() => {
    if (!edificioId || !selectedPeriodo || Object.keys(tableData).length === 0) return;
    // Guard anti-pisado: no auto-crear/guardar hasta que los resultados persistidos
    // estén cargados. Si corre antes, las unidades existentes se ven como "nuevas"
    // (sin resultado_id) y el createBatch pisaría el ingreso manual y usa_manual.
    if (!resultadosCargados) return;
    const saveKey = `${edificioId}-${selectedPeriodo}`;
    if (autoSavedPeriods.current.has(saveKey)) return;
    
    const toSave = [];
    Object.entries(tableData).forEach(([unidadId, data]) => {
      const ingreso = parseFloat(data.ingreso) || 0;
      const unidad = unidades.find(u => u.unidad_id === unidadId);
      const tipoAdmin = unidad?.tipo_administracion || 'full';
      const egresoActual = tipoAdmin === 'full' ? (parseFloat(data.egreso) || 0) : 0;

      if (ingreso > 0 && !data.resultado_id) {
        // New record - needs saving
        toSave.push({
          unidad_id: unidadId,
          ingreso: { monto: ingreso, moneda: data.moneda || 'ARS' },
          egreso: { monto: egresoActual, moneda: data.moneda || 'ARS' },
          fee_administracion: { monto: parseFloat(data.fee) || 0, moneda: data.moneda || 'ARS' },
          rentabilidad_anual_proyectada_pct: data.rentabilidad ? parseFloat(data.rentabilidad) : null,
          notas: data.notas || null,
          usa_manual: data.usa_manual === true || data.ingresoManual === true,
          ganancia_neta_manual: null,
        });
      }
    });
    if (toSave.length === 0) return;
    autoSavedPeriods.current.add(saveKey);
    
    const saveAll = async () => {
      try {
        const res = await resultadosApi.createBatch({
          edificio_id: edificioId,
          periodo: selectedPeriodo,
          resultados: toSave
        });
        const saved = res.data?.resultados || [];
        if (saved.length > 0) {
          setTableData(prev => {
            const updated = { ...prev };
            saved.forEach(s => {
              if (updated[s.unidad_id]) {
                updated[s.unidad_id] = { ...updated[s.unidad_id], resultado_id: s.resultado_id, estado: updated[s.unidad_id].estado || 'borrador' };
              }
            });
            return updated;
          });
          // Sincronizar `resultados` local con lo recién creado (mismo motivo que en autoSaveUnit).
          setResultados(prev => {
            const copy = [...prev];
            saved.forEach(s => {
              const src = toSave.find(t => t.unidad_id === s.unidad_id);
              if (!src) return;
              const idx = copy.findIndex(r => r.unidad_id === s.unidad_id && r.periodo === selectedPeriodo);
              const base = idx >= 0 ? copy[idx] : {};
              const merged = {
                ...base,
                resultado_id: s.resultado_id,
                unidad_id: s.unidad_id,
                periodo: selectedPeriodo,
                ingreso: src.ingreso,
                egreso: src.egreso,
                fee_administracion: src.fee_administracion,
                usa_manual: src.usa_manual === true,
                rentabilidad_anual_proyectada_pct: src.rentabilidad_anual_proyectada_pct ?? (base.rentabilidad_anual_proyectada_pct ?? null),
                notas: src.notas ?? base.notas ?? null,
                estado: base.estado || 'borrador',
              };
              if (idx >= 0) copy[idx] = merged; else copy.push(merged);
            });
            return copy;
          });
        }
      } catch (err) {
        console.error('Error auto-saving:', err);
        autoSavedPeriods.current.delete(saveKey);
      }
    };
    saveAll();
  }, [tableData, edificioId, selectedPeriodo, resultadosCargados]);

  // Precargar ingresos desde facturación y egresos cuando cambie el período
  useEffect(() => {
    const precargarDatos = async () => {
      if (!selectedPeriodo || !edificioId || isComercial) return;
      
      setCargandoAlquileres(true);
      const nuevosAlquileres = {};
      const montoNetoMap = {};
      
      // Mapa de cocheras asociadas: { cochera_id: { oficina_madre_id, ingreso, egreso, nombre } }
      const cocherasAsociadas = {};
      const cocherasDeOtraUnidad = [];
      const externasMap = {};
      try {
        // Obtener datos de facturación del período
        const res = await facturacionApi.getMensual(edificioId, selectedPeriodo);
        const items = res.data?.items || [];
        
        // Obtener datos de egresos del período
        let egresosDelPeriodo = {};
        try {
          const resEgresos = await resultadosApi.getEgresos(edificioId, selectedPeriodo);
          egresosDelPeriodo = resEgresos.data || {};
        } catch (err) {
          console.error('Error cargando egresos:', err);
        }
        
        // Mapear los montos netos por unidad
        // (cocherasAsociadas se acumula durante este forEach)
        items.forEach(item => {
          // Monto neto FULL del item = fuente de verdad de 5.Factura (clave: unidad principal).
          montoNetoMap[item.unidad_id] = parseFloat(item.monto_neto) || 0;
          if (item.es_multicontrato && item.unidades_factura && item.unidades_factura.length > 0) {
            const montoNetoTotal = parseFloat(item.monto_neto) || 0;
            const ufs = item.unidades_factura;
            // 1) Sumar ingreso esperado para externas (valor estándar cochera)
            const sumExternas = ufs.reduce((s, uf) => s + (uf.rol === 'cochera_externa' ? (parseFloat(uf.monto) || 0) : 0), 0);
            // 2) Suma de cocheras asociadas (van DENTRO de la oficina madre)
            const sumAsociadas = ufs.reduce((s, uf) => s + (uf.rol === 'cochera_asociada' ? (parseFloat(uf.monto) || 0) : 0), 0);
            const madreId = (ufs.find(uf => uf.rol === 'oficina_madre') || {}).unidad_id || item.unidad_id;
            if (sumExternas > 0) externasMap[madreId] = (externasMap[madreId] || 0) + sumExternas;

            ufs.forEach(uf => {
              const keyEgreso = `${uf.unidad_id}-${selectedPeriodo}`;
              const egresosUnidad = egresosDelPeriodo[keyEgreso] || {};
              const totalEgresos = (
                (parseFloat(egresosUnidad.retenciones) || 0) +
                (parseFloat(egresosUnidad.iibb) || 0) +
                (parseFloat(egresosUnidad.expensas) || 0) +
                (parseFloat(egresosUnidad.edemsa) || 0) +
                (parseFloat(egresosUnidad.tasa_municipal) || 0) +
                (parseFloat(egresosUnidad.agua) || 0) +
                (parseFloat(egresosUnidad.gastos_negocio) || 0)
              );

              if (uf.rol === 'oficina_madre') {
                // Ingreso oficina = monto_neto del contrato − cocheras externas (las asociadas quedan adentro)
                const ingresoOficina = Math.max(0, montoNetoTotal - sumExternas);
                nuevosAlquileres[uf.unidad_id] = {
                  monto: Math.round(ingresoOficina * 100) / 100,
                  egreso: totalEgresos,  // se sumará el egreso de las asociadas más abajo
                  moneda: item.moneda || 'ARS',
                  inquilino: item.inquilino,
                  mensaje: sumAsociadas > 0
                    ? `Incluye ingreso de cochera asociada ($${sumAsociadas.toLocaleString('es-AR')})`
                    : 'Residual oficina del contrato',
                  contrato_id: item.contrato_id,
                };
              } else if (uf.rol === 'cochera_asociada') {
                // Cochera asociada: NO crea fila propia. Guardar para sumar egresos a la oficina madre.
                cocherasAsociadas[uf.unidad_id] = {
                  oficina_madre_id: uf.oficina_madre_id,
                  ingreso_cochera: parseFloat(uf.monto) || 0,
                  egreso_cochera: totalEgresos,
                  nombre: uf.nombre_unidad,
                };
              } else if (uf.rol === 'cochera_externa' && uf.unidad_principal_id && uf.unidad_principal_id !== uf.oficina_madre_id) {
                // Cochera externa que es parte de OTRA unidad (ej. C005 → 303): su alquiler
                // es ganancia del dueño de esa unidad; se suma a su ingreso más abajo.
                cocherasDeOtraUnidad.push({
                  principal_id: uf.unidad_principal_id,
                  unidad_id: uf.unidad_id,
                  nombre: uf.nombre_unidad,
                  ingreso: parseFloat(uf.monto) || 0,
                  inquilino: item.inquilino,
                });
              } else if (uf.rol === 'cochera_externa') {
                // Cochera externa: fila propia con valor estándar (fijo, sin IPC)
                nuevosAlquileres[uf.unidad_id] = {
                  monto: parseFloat(uf.monto) || 0,
                  egreso: totalEgresos,
                  moneda: item.moneda || 'ARS',
                  inquilino: item.inquilino,
                  mensaje: 'Valor estándar cochera',
                  contrato_id: item.contrato_id,
                };
              } else {
                // Otros tipos secundarios
                nuevosAlquileres[uf.unidad_id] = {
                  monto: parseFloat(uf.monto) || 0,
                  egreso: totalEgresos,
                  moneda: item.moneda || 'ARS',
                  inquilino: item.inquilino,
                  mensaje: 'Unidad secundaria del contrato',
                  contrato_id: item.contrato_id,
                };
              }
            });
          } else {
            // Contrato normal de una sola unidad
            const keyEgreso = `${item.unidad_id}-${selectedPeriodo}`;
            const egresosUnidad = egresosDelPeriodo[keyEgreso] || {};
            const totalEgresos = (
              (parseFloat(egresosUnidad.retenciones) || 0) +
              (parseFloat(egresosUnidad.iibb) || 0) +
              (parseFloat(egresosUnidad.expensas) || 0) +
              (parseFloat(egresosUnidad.edemsa) || 0) +
              (parseFloat(egresosUnidad.tasa_municipal) || 0) +
              (parseFloat(egresosUnidad.agua) || 0) +
              (parseFloat(egresosUnidad.gastos_negocio) || 0)
            );

            if (item.tiene_contrato && item.monto_neto > 0) {
              // Tab 9 es proyectiva (liquidación): muestra el ingreso esperado del período
              // independientemente del estado de cobranza (pendiente/cobrada/etc.).
              nuevosAlquileres[item.unidad_id] = {
                monto: item.monto_neto,
                egreso: totalEgresos,
                moneda: item.moneda || 'ARS',
                inquilino: item.inquilino,
                mensaje: 'Monto del contrato',
                contrato_id: item.contrato_id,
              };
            } else if (item.tiene_contrato && item.monto_neto === 0) {
              // Contrato existe pero factura no generada aún - ingreso 0
              nuevosAlquileres[item.unidad_id] = {
                monto: 0,
                egreso: totalEgresos,
                moneda: item.moneda || 'ARS',
                inquilino: item.inquilino,
                mensaje: 'Sin facturación'
              };
            } else if (totalEgresos > 0) {
              nuevosAlquileres[item.unidad_id] = {
                monto: 0,
                egreso: totalEgresos,
                moneda: 'ARS',
                inquilino: null,
                mensaje: 'Solo egresos'
              };
            }
          }
        });
        
        // También agregar unidades que tienen egresos pero no están en facturación
        Object.keys(egresosDelPeriodo).forEach(key => {
          const [unidadId] = key.split('-');
          if (!nuevosAlquileres[unidadId]) {
            const egresosUnidad = egresosDelPeriodo[key] || {};
            const totalEgresos = (
              (parseFloat(egresosUnidad.retenciones) || 0) +
              (parseFloat(egresosUnidad.iibb) || 0) +
              (parseFloat(egresosUnidad.expensas) || 0) +
              (parseFloat(egresosUnidad.edemsa) || 0) +
              (parseFloat(egresosUnidad.tasa_municipal) || 0) +
              (parseFloat(egresosUnidad.agua) || 0) +
              (parseFloat(egresosUnidad.gastos_negocio) || 0)
            );
            if (totalEgresos > 0) {
              nuevosAlquileres[unidadId] = {
                monto: 0,
                egreso: totalEgresos,
                moneda: 'ARS',
                inquilino: null,
                mensaje: 'Solo egresos'
              };
            }
          }
        });
        // Cocheras alquiladas en otro contrato que pertenecen a una unidad: suman a su ingreso.
        cocherasDeOtraUnidad.forEach(c => {
          const p = nuevosAlquileres[c.principal_id] || (nuevosAlquileres[c.principal_id] = {
            monto: 0, egreso: 0, moneda: 'ARS', inquilino: null, mensaje: 'Solo cochera alquilada',
          });
          p.monto = Math.round(((parseFloat(p.monto) || 0) + c.ingreso) * 100) / 100;
          p.mensaje = `${p.mensaje ? p.mensaje + ' · ' : ''}Incluye cochera ${c.nombre} alquilada a ${c.inquilino || 'otro inquilino'} ($${c.ingreso.toLocaleString('es-AR')})`;
        });
      } catch (err) {
        console.error('Error cargando facturación:', err);
      }

      // Consolidar cocheras asociadas en su oficina madre: solo egreso + detalle
      // (el ingreso ya está incluido en el residual de la oficina madre)
      const mapAsoc = {};
      const detalleByMadre = {};
      Object.entries(cocherasAsociadas).forEach(([cocheraId, info]) => {
        mapAsoc[cocheraId] = info.oficina_madre_id;
        const madre = nuevosAlquileres[info.oficina_madre_id];
        if (madre) {
          // NO sumar ingreso al madre (ya está dentro del residual)
          madre.egreso = (madre.egreso || 0) + (info.egreso_cochera || 0);
          madre.cocheras_asociadas = madre.cocheras_asociadas || [];
          madre.cocheras_asociadas.push({
            unidad_id: cocheraId,
            nombre: info.nombre,
            ingreso: info.ingreso_cochera,
            egreso: info.egreso_cochera,
          });
          detalleByMadre[info.oficina_madre_id] = detalleByMadre[info.oficina_madre_id] || [];
          detalleByMadre[info.oficina_madre_id].push({
            unidad_id: cocheraId,
            nombre: info.nombre,
            ingreso: info.ingreso_cochera,
            egreso: info.egreso_cochera,
          });
        }
      });
      setCocherasAsociadasMap(mapAsoc);
      setCocherasDetalleByMadre(detalleByMadre);
      setAlquileresPrecargados(nuevosAlquileres);
      setMontoNetoByUnidad(montoNetoMap);
      setExternasByMadre(externasMap);
      setCargandoAlquileres(false);
    };
    
    precargarDatos();
  }, [selectedPeriodo, edificioId, isComercial]);

  // Cargar cotización cuando cambie el período
  useEffect(() => {
    const fetchCotizacion = async () => {
      try {
        const res = await cotizacionesApi.list();
        const cotizacion = res.data.find(c => c.periodo === selectedPeriodo);
        setCotizacionPeriodo(cotizacion);
      } catch (err) {
        console.error('Error fetching cotizacion:', err);
        setCotizacionPeriodo(null);
      }
    };
    fetchCotizacion();
  }, [selectedPeriodo]);

  // Cargar indicadores de facturación siempre (para badge morosos)
  useEffect(() => {
    const fetchIndicadores = async () => {
      if (!edificioId || isComercial) return;
      try {
        const res = await facturacionApi.getIndicadores(edificioId);
        setIndicadoresFacturacion(res.data);
      } catch (err) {
        console.error('Error fetching indicadores:', err);
      }
    };
    fetchIndicadores();
  }, [edificioId, isComercial]);

  // Cargar datos de facturación cuando se selecciona la pestaña (también para Egresos para calcular IIBB)
  useEffect(() => {
    const fetchFacturacion = async () => {
      if (!['factura', 'cobro', 'egresos'].includes(activeTab) || !edificioId || isComercial) return;
      
      setLoadingFacturacion(true);
      try {
        const [resData, resIndicadores] = await Promise.all([
          facturacionApi.getMensual(edificioId, selectedPeriodo),
          facturacionApi.getIndicadores(edificioId)
        ]);
        setFacturacionData(resData.data);
        setIndicadoresFacturacion(resIndicadores.data);
      } catch (err) {
        console.error('Error fetching facturacion:', err);
        setFacturacionData(null);
      } finally {
        setLoadingFacturacion(false);
      }
    };
    fetchFacturacion();
  }, [activeTab, edificioId, selectedPeriodo, isComercial]);

  // Cargar datos de morosos
  useEffect(() => {
    const fetchMorosos = async () => {
      if (activeTab !== 'morosos' || !edificioId || isComercial) return;
      
      setLoadingMorosos(true);
      try {
        const res = await facturacionApi.getMorosos(edificioId);
        setMorososData(res.data);
      } catch (err) {
        console.error('Error fetching morosos:', err);
        setMorososData(null);
      } finally {
        setLoadingMorosos(false);
      }
    };
    fetchMorosos();
  }, [activeTab, edificioId, isComercial]);

  // Cargar datos de históricos
  useEffect(() => {
    const fetchHistoricos = async () => {
      if (activeTab !== 'historicos' || !edificioId) return;
      
      setLoadingHistoricos(true);
      try {
        const res = await historialApi.getByEdificio(edificioId);
        setHistoricosData(res.data || []);
      } catch (err) {
        console.error('Error fetching historicos:', err);
        setHistoricosData([]);
      } finally {
        setLoadingHistoricos(false);
      }
    };
    fetchHistoricos();
  }, [activeTab, edificioId]);

  // Cargar egresos cuando cambia el período (necesario para tab 7 y tab 8 Liquidación)
  useEffect(() => {
    const fetchEgresos = async () => {
      if (!edificioId || !selectedPeriodo || isComercial) return;
      
      try {
        const res = await resultadosApi.getEgresos(edificioId, selectedPeriodo);
        setEgresosData(res.data || {});
        setEgresosHasChanges(false);
      } catch (err) {
        console.error('Error fetching egresos:', err);
        setEgresosData({});
      }
    };
    fetchEgresos();
  }, [edificioId, selectedPeriodo, isComercial]);

  // Fetch expensas resumen (liquidación aprobada) para incluir en el cálculo de egresos.
  // Esto refleja lo que muestra el tab "8. Egresos": expensas + iibb auto-calculado.
  useEffect(() => {
    if (!edificioId || !selectedPeriodo || isComercial) {
      setExpensasLiqMap({}); setExpensasLiqEstado(null); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await expensasApi.getExpensasResumenPorMes(edificioId, selectedPeriodo);
        if (cancelled) return;
        if (r.data?.existe && r.data.estado === 'aprobado') {
          const raw = r.data.expensas_para_liquidacion || r.data.liquidaciones_por_unidad || {};
          const normalized = {};
          for (const [k, v] of Object.entries(raw)) {
            normalized[k] = Math.abs(parseFloat(v) || 0);
          }
          setExpensasLiqMap(normalized);
          setExpensasLiqEstado('aprobado');
        } else {
          setExpensasLiqMap({});
          setExpensasLiqEstado(r.data?.estado || null);
        }
      } catch {
        if (!cancelled) { setExpensasLiqMap({}); setExpensasLiqEstado(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [edificioId, selectedPeriodo, isComercial]);

  // Función para sincronizar cobros con Colppy
  const handleSyncColppy = async () => {
    if (!edificioId || !selectedPeriodo) return;
    
    setSyncingColppy(true);
    try {
      const res = await colppyApi.sincronizarCobros(edificioId, selectedPeriodo);
      
      if (res.data.ok) {
        const { actualizados, ya_cobrados, sin_cliente_colppy, errores } = res.data.resumen;
        
        if (actualizados > 0) {
          toast.success(`Sincronización completada: ${actualizados} cobro(s) actualizado(s)`);
          // Recargar datos de facturación
          const facRes = await facturacionApi.getMensual(edificioId, selectedPeriodo);
          setFacturacionData(facRes.data);
        } else if (ya_cobrados > 0 && actualizados === 0) {
          toast.info('Todos los cobros ya estaban sincronizados');
        } else if (sin_cliente_colppy > 0) {
          toast.warning(`${sin_cliente_colppy} unidad(es) sin cliente en Colppy`);
        } else {
          toast.info('No se encontraron cobros para actualizar');
        }
        
        // Mostrar errores si los hay
        if (errores > 0) {
          toast.error(`${errores} error(es) durante la sincronización`);
        }
      } else {
        toast.error('Error al sincronizar con Colppy');
      }
    } catch (err) {
      console.error('Error syncing with Colppy:', err);
      toast.error('Error de conexión con Colppy');
    } finally {
      setSyncingColppy(false);
    }
  };

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [edificioRes, unidadesRes, propietariosRes, gruposRes, contratosRes] = await Promise.all([
        edificiosApi.get(edificioId),
        unidadesApi.list({ edificio_id: edificioId }),
        propietariosApi.list(),
        gruposUnidadesApi.list(edificioId),
        contratosApi.list({ edificio_id: edificioId }),
      ]);
      
      setEdificio(edificioRes.data);
      setUnidades(unidadesRes.data);
      setPropietarios(propietariosRes.data);
      setGrupos(gruposRes.data || []);
      setContratos(contratosRes.data || []);
      
      // Fetch resultados for this edificio's units
      const resultadosRes = await resultadosApi.list({ edificio_id: edificioId });
      setResultados(resultadosRes.data);
      setResultadosCargados(true);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Helper: ordenar unidades por prioridad de tipo y piso
  // Orden: oficinas (por piso/número) > cowork > locales (por número) > cocheras
  const sortUnidades = (list) => {
    const tipoPrioridad = { oficina: 0, cowork: 1, local: 2, cochera: 3 };

    const extractPiso = (nombre) => {
      // "Piso 5" -> 5, "201 (ex 212)" -> 2, "Oficina 3A" -> 3, "Local 10" / "C001" -> sin piso
      const pisoMatch = nombre.match(/piso\s*(\d+)/i);
      if (pisoMatch) return parseInt(pisoMatch[1]);
      const numMatch = nombre.match(/^(\d)/);
      if (numMatch) return parseInt(numMatch[1]);
      return 99;
    };

    const extractNumero = (nombre) => {
      // "201 (ex 212)" -> 201, "101" -> 101, "Local 10" -> 10, "Piso 5" -> 500, "C001" -> 1
      const leadingNum = nombre.match(/^(\d+)/);
      if (leadingNum) return parseInt(leadingNum[1]);
      const pisoMatch = nombre.match(/piso\s*(\d+)/i);
      if (pisoMatch) return parseInt(pisoMatch[1]) * 100;
      const cMatch = nombre.match(/[A-Za-z]+\s*(\d+)/);
      if (cMatch) return parseInt(cMatch[1]);
      return 9999;
    };

    return [...list].sort((a, b) => {
      const tipoA = tipoPrioridad[a.tipo] ?? 99;
      const tipoB = tipoPrioridad[b.tipo] ?? 99;
      if (tipoA !== tipoB) return tipoA - tipoB;
      const pisoA = extractPiso(a.nombre_unidad);
      const pisoB = extractPiso(b.nombre_unidad);
      if (pisoA !== pisoB) return pisoA - pisoB;
      return extractNumero(a.nombre_unidad) - extractNumero(b.nombre_unidad);
    });
  };

  // Helper: orden de unidades respetando agrupaciones para tabs 4, 5, 6
  const getGroupedOrder = (unitsList) => {
    if (grupos.length === 0) {
      return unitsList.map(u => ({ type: 'unit', unidad: u }));
    }
    
    const result = [];
    const processedIds = new Set();
    
    grupos.forEach(grupo => {
      const grupoUnidades = grupo.unidad_ids
        .map(id => unitsList.find(u => u.unidad_id === id))
        .filter(Boolean);
      if (grupoUnidades.length === 0) return;
      result.push({ type: 'group_summary', grupo, unidades: grupoUnidades });
      if (expandedGrupos[grupo.grupo_id]) {
        grupoUnidades.forEach(u => result.push({ type: 'unit', unidad: u, grupoId: grupo.grupo_id }));
      }
      grupoUnidades.forEach(u => processedIds.add(u.unidad_id));
    });
    
    unitsList
      .filter(u => !processedIds.has(u.unidad_id))
      .forEach(u => result.push({ type: 'unit', unidad: u }));
    
    return result;
  };

  // Same for facturacion items
  const getGroupedFacItems = (items) => {
    if (grupos.length === 0) {
      return items.map(i => ({ type: 'item', item: i }));
    }
    
    const result = [];
    const processedIds = new Set();
    
    // Primero procesar grupos con 2+ items (desplegables)
    grupos.forEach(grupo => {
      const grupoItems = grupo.unidad_ids
        .map(id => items.find(i => i.unidad_id === id))
        .filter(Boolean);
      if (grupoItems.length <= 1) return;
      result.push({ type: 'group_summary', grupo, items: grupoItems });
      if (expandedGrupos[grupo.grupo_id]) {
        grupoItems.forEach(item => result.push({ type: 'item', item, grupoId: grupo.grupo_id }));
      }
      grupoItems.forEach(item => processedIds.add(item.unidad_id));
    });
    
    // Marcar items de grupos de 1 como procesados pero no agregar todavia
    grupos.forEach(grupo => {
      const grupoItems = grupo.unidad_ids
        .map(id => items.find(i => i.unidad_id === id))
        .filter(Boolean);
      if (grupoItems.length === 1) {
        processedIds.add(grupoItems[0].unidad_id);
      }
    });
    
    // Agregar todos los items individuales (no agrupados + grupos de 1) en orden original
    items
      .filter(i => !processedIds.has(i.unidad_id) || grupos.some(g => g.unidad_ids.includes(i.unidad_id) && g.unidad_ids.map(id => items.find(it => it.unidad_id === id)).filter(Boolean).length === 1))
      .forEach(i => result.push({ type: 'item', item: i }));
    
    return result;
  };

  // Helper: calcular total de egresos de una unidad
  // Consistente con TabEgresos.jsx: incluye expensas auto (liquidación aprobada), IIBB auto (4% del ingreso) y retención auto (% del contrato vigente).
  const calcEgresoFromState = (unidadId) => {
    const key = `${unidadId}-${selectedPeriodo}`;
    const d = egresosData[key] || {};
    // Ingreso de la unidad (para calcular IIBB y retención automáticos si no hay manual)
    const ingreso = (() => {
      // Neto del período seleccionado (siempre cargado); facturacionData puede ser de otro mes.
      if (montoNetoByUnidad[unidadId] !== undefined) return montoNetoByUnidad[unidadId] || 0;
      if (!facturacionData?.items) return 0;
      const item = facturacionData.items.find(i => i.unidad_id === unidadId);
      return item?.monto_neto || 0;
    })();
    // Retención: manual o auto desde contrato vigente.
    const retManual = d.retenciones;
    let retenciones = 0;
    if (d.retenciones_manual && retManual !== undefined && retManual !== '' && retManual !== null) {
      retenciones = parseFloat(retManual) || 0;
    } else {
      // Buscar contrato vigente que incluya esta unidad
      const contratoVigente = (contratos || []).find(c => {
        if (c.estado !== 'vigente') return false;
        if (c.unidad_id === unidadId) return true;
        return (c.unidades_contrato || []).some(uc => uc.unidad_id === unidadId);
      });
      if (contratoVigente) {
        const tipo = contratoVigente.retencion_tipo || 'sin_retenciones';
        let pct = 0;
        if (tipo === '2') pct = 2;
        else if (tipo === '4') pct = 4;
        else if (tipo === '6') pct = 6;
        else if (tipo === 'otro') pct = parseFloat(contratoVigente.retencion_porcentaje) || 0;
        retenciones = Math.round(ingreso * pct) / 100;
      }
    }
    // IIBB: manual o auto = 4% del ingreso
    const iibbManual = d.iibb_manual ? d.iibb : undefined;
    const iibb = (iibbManual !== undefined && iibbManual !== '' && iibbManual !== null)
      ? parseFloat(iibbManual)
      : Math.round(ingreso * 0.04 * 100) / 100;
    // Expensas: manual (si el analista la editó) tiene prioridad; sino liquidación aprobada.
    let expensas = 0;
    if (d.expensas_manual && d.expensas !== undefined && d.expensas !== '' && d.expensas !== null) {
      expensas = Math.abs(parseFloat(d.expensas) || 0);
    } else if (expensasLiqEstado === 'aprobado') {
      expensas = expensasLiqMap[unidadId] || 0;
    }
    const total = retenciones +
      (iibb || 0) +
      expensas +
      (parseFloat(d.edemsa) || 0) +
      (parseFloat(d.tasa_municipal) || 0) +
      (parseFloat(d.agua) || 0) +
      (parseFloat(d.gastos_negocio) || 0);
    if (total === 0 && !egresosData[key]) return null;
    return total;
  };

  // Retenciones + IIBB de una unidad (misma lógica que calcEgresoFromState, sin expensas ni
  // servicios). Se usa para replicar exactamente el total del bloque Piso 1 de 8.Egresos.
  const calcRetIibbFromState = (unidadId) => {
    const key = `${unidadId}-${selectedPeriodo}`;
    const d = egresosData[key] || {};
    const ingreso = (() => {
      // Neto del período seleccionado (siempre cargado); facturacionData puede ser de otro mes.
      if (montoNetoByUnidad[unidadId] !== undefined) return montoNetoByUnidad[unidadId] || 0;
      if (!facturacionData?.items) return 0;
      const item = facturacionData.items.find(i => i.unidad_id === unidadId);
      return item?.monto_neto || 0;
    })();
    const retManual = d.retenciones;
    let retenciones = 0;
    if (d.retenciones_manual && retManual !== undefined && retManual !== '' && retManual !== null) {
      retenciones = parseFloat(retManual) || 0;
    } else {
      const contratoVigente = (contratos || []).find(c => {
        if (c.estado !== 'vigente') return false;
        if (c.unidad_id === unidadId) return true;
        return (c.unidades_contrato || []).some(uc => uc.unidad_id === unidadId);
      });
      if (contratoVigente) {
        const tipo = contratoVigente.retencion_tipo || 'sin_retenciones';
        let pct = 0;
        if (tipo === '2') pct = 2;
        else if (tipo === '4') pct = 4;
        else if (tipo === '6') pct = 6;
        else if (tipo === 'otro') pct = parseFloat(contratoVigente.retencion_porcentaje) || 0;
        retenciones = Math.round(ingreso * pct) / 100;
      }
    }
    const iibbManual = d.iibb_manual ? d.iibb : undefined;
    const iibb = (iibbManual !== undefined && iibbManual !== '' && iibbManual !== null)
      ? (parseFloat(iibbManual) || 0)
      : Math.round(ingreso * 0.04 * 100) / 100;
    return retenciones + (iibb || 0);
  };


  const buildTableData = () => {
    const newTableData = {};
    
    unidades.forEach(unidad => {
      // Find existing resultado for this unidad and periodo
      const existingResultado = resultados.find(
        r => r.unidad_id === unidad.unidad_id && r.periodo === selectedPeriodo
      );
      
      // Obtener el porcentaje de administración de la unidad
      const tipoAdmin = unidad.tipo_administracion || 'full';
      const porcentaje = unidad.porcentaje_administracion ?? (tipoAdmin === 'full' ? 20 : 8);
      
      // Prioridad para egresos: 1) estado en vivo de tab 7, 2) alquileresPrecargados, 3) resultado existente
      const egresoFromState = calcEgresoFromState(unidad.unidad_id);
      const alquilerPrecargado = alquileresPrecargados[unidad.unidad_id];
      const egresoFinal = egresoFromState ?? alquilerPrecargado?.egreso ?? 0;
      
      if (existingResultado) {
        // Fuente de verdad del flag manual = usa_manual (persistido por el backend).
        const esManual = existingResultado.usa_manual === true;
        const esPublicado = existingResultado.estado === 'publicado';

        let ingresoSaneado, feeSaneado, egresoParaFila;

        if (esPublicado) {
          // PUBLICADO: conservar EXACTAMENTE lo persistido. Sin precarga de Facturación,
          // sin recálculo de fee, sin saneo. Nada automático toca un período publicado.
          ingresoSaneado = existingResultado.ingreso?.monto ?? '';
          feeSaneado = existingResultado.fee_administracion?.monto ?? '';
          egresoParaFila = existingResultado.egreso?.monto ?? '';
        } else {
          // BORRADOR: si el ingreso es manual, NO se pisa con Facturación.
          const aplicarPrecarga = alquilerPrecargado != null && !esManual;
          const ingresoFinal = aplicarPrecarga
            ? (alquilerPrecargado.monto || 0)
            : (existingResultado.ingreso?.monto || '');

          let feeFinal = existingResultado.fee_administracion?.monto;
          const ingParaFee = parseFloat(aplicarPrecarga ? ingresoFinal : (ingresoFinal || 0)) || 0;
          const egParaFee = parseFloat(egresoFinal) || 0;
          const feeVacio = feeFinal === undefined || feeFinal === null || feeFinal === '';
          // Calcular el fee si viene de precarga, o si no hay fee guardado pero sí hay ingreso.
          // (No pisa un fee cargado a mano; no aplica a períodos publicados.)
          if (aplicarPrecarga || feeVacio) {
            if (ingParaFee > 0 && porcentaje > 0) {
              feeFinal = tipoAdmin === 'full'
                ? (ingParaFee - egParaFee > 0 ? Math.round((ingParaFee - egParaFee) * (porcentaje / 100)) : 0)
                : Math.round(ingParaFee * (porcentaje / 100));
            } else if (aplicarPrecarga) {
              feeFinal = 0;
            }
          }

          ingresoSaneado = ingresoFinal;
          feeSaneado = feeFinal ?? '';
          // Guard contra ingresos fantasma: sin contrato vigente y no-manual → limpiar montos.
          if (alquilerPrecargado == null && !esManual) {
            ingresoSaneado = '';
            feeSaneado = '';
          }
          egresoParaFila = egresoFinal || existingResultado.egreso?.monto || '';
        }

        const data = {
          resultado_id: existingResultado.resultado_id,
          ingreso: ingresoSaneado,
          egreso: egresoParaFila,
          fee: feeSaneado,
          rentabilidad: existingResultado.rentabilidad_anual_proyectada_pct || '',
          notas: existingResultado.notas || '',
          moneda: existingResultado.ingreso?.moneda || 'ARS',
          estado: existingResultado.estado,
          usa_manual: esManual,
          archivos: existingResultado.archivos || {},
          renta_email_enviada_at: existingResultado.renta_email_enviada_at || null,
          renta_email_error: existingResultado.renta_email_error || null,
          factura_propietario: existingResultado.factura_propietario || null,
        };
        
        // Si no hay rentabilidad guardada pero hay ingreso, intentar calcularla
        if (!data.rentabilidad && data.ingreso) {
          const rentCalculada = calculateRentabilidadSync(unidad, data);
          if (rentCalculada) {
            data.rentabilidad = rentCalculada;
          }
        }
        
        newTableData[unidad.unidad_id] = data;
      } else {
        // Si no hay resultado, precargar alquiler y egresos si están disponibles
        const ingreso = alquilerPrecargado?.monto || 0;
        const egreso = egresoFinal;
        
        // Calcular fee automáticamente basado en la fórmula según el tipo de administración
        let feeCalculado = '';
        if (ingreso > 0 && porcentaje > 0) {
          if (tipoAdmin === 'full') {
            const rentaBase = ingreso - egreso;
            feeCalculado = rentaBase > 0 ? Math.round(rentaBase * (porcentaje / 100)) : 0;
          } else {
            feeCalculado = Math.round(ingreso * (porcentaje / 100));
          }
        }
        
        // Calcular rentabilidad
        let rentabilidad = '';
        if (ingreso > 0) {
          const rentCalc = calculateRentabilidadSync(unidad, { ingreso, moneda: alquilerPrecargado?.moneda || 'ARS' });
          if (rentCalc) rentabilidad = rentCalc;
        }
        
        newTableData[unidad.unidad_id] = {
          resultado_id: null,
          ingreso: ingreso || '',
          egreso: egreso || '',
          fee: feeCalculado,
          rentabilidad,
          notas: alquilerPrecargado ? `Precargado: ${alquilerPrecargado.mensaje}` : '',
          moneda: alquilerPrecargado?.moneda || 'ARS',
          estado: null,
          archivos: {},
          factura_propietario: null,
          alquilerPrecargado: !!alquilerPrecargado,
        };
      }
    });
    
    // ─── Torre Emilia: Piso 1 consolidado ───
    // Sobrescribir tableData[unit_piso1_te] con los valores agregados de sus miembros para que:
    //   1) la fila Piso 1 muestre la rendición consolidada,
    //   2) el auto-save genere un único resultado_mensual para unit_piso1_te (1 rendición x propietario),
    //   3) la columna Estado funcione igual que para cualquier otra unidad.
    const esTE = edificio?.modelo_administracion === 'gc_factura_y_cobra';
    const PISO1_ID = 'unit_piso1_te';
    if (esTE && newTableData[PISO1_ID]) {
      const miembros = unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID);
      // Fuente de verdad del INGRESO: monto neto FULL de 5.Factura por unidad principal
      // (montoNetoByUnidad), NUNCA resultados guardados ni el split por unidad (que puede
      // quedar corrupto). Así 9.Liquidación coincide exactamente con 5.Factura.
      // Menos las cocheras de otros dueños alquiladas en esos contratos (se rinden a su dueño).
      const sumIngreso = miembros.reduce((s, m) => s + Math.max(0, (parseFloat(montoNetoByUnidad[m.unidad_id]) || 0) - (externasByMadre[m.unidad_id] || 0)), 0);
      // Egreso del Piso 1 = RÉPLICA EXACTA del total del bloque en 8.Egresos (totalBloque):
      //   Σ miembros(retenciones) + Σ miembros(IIBB)
      //   + edemsa/tasa/agua/gastos PROPIOS del bloque (unit_piso1_te)
      //   + expensas (liquidación aprobada) de [bloque + miembros].
      // Los servicios (edemsa/tasa/agua/gastos) y las expensas se cargan a nivel bloque
      // en 8.Egresos; NO se toman de los miembros (por eso no se usa calcEgresoFromState acá).
      const sumRetIibb = miembros.reduce((s, m) => s + calcRetIibbFromState(m.unidad_id), 0);
      const bloqueData = egresosData[`${PISO1_ID}-${selectedPeriodo}`] || {};
      const bloqueServicios =
        (parseFloat(bloqueData.edemsa) || 0) +
        (parseFloat(bloqueData.tasa_municipal) || 0) +
        (parseFloat(bloqueData.agua) || 0) +
        (parseFloat(bloqueData.gastos_negocio) || 0);
      const expensasBloque = (expensasLiqEstado === 'aprobado')
        ? [PISO1_ID, ...miembros.map(m => m.unidad_id)].reduce((s, id) => s + Math.abs(parseFloat(expensasLiqMap[id]) || 0), 0)
        : 0;
      const sumEgreso = sumRetIibb + bloqueServicios + expensasBloque;
      // Fee consolidado: 20% sobre (ingreso - egreso) si es positivo; sino 0.
      const tipoAdminPiso1 = newTableData[PISO1_ID].alquilerPrecargado != null
        ? (unidades.find(u => u.unidad_id === PISO1_ID)?.tipo_administracion || 'full')
        : (unidades.find(u => u.unidad_id === PISO1_ID)?.tipo_administracion || 'full');
      const porcentajePiso1 = unidades.find(u => u.unidad_id === PISO1_ID)?.porcentaje_administracion ?? 20;
      let feeConsolidado = 0;
      if (sumIngreso > 0 && porcentajePiso1 > 0) {
        if (tipoAdminPiso1 === 'full') {
          const rentaBase = sumIngreso - sumEgreso;
          feeConsolidado = rentaBase > 0 ? Math.round(rentaBase * (porcentajePiso1 / 100)) : 0;
        } else {
          feeConsolidado = Math.round(sumIngreso * (porcentajePiso1 / 100));
        }
      }
      // Rentabilidad consolidada (si Piso 1 tiene monto_inversion definido en la unidad sintética).
      const unidadPiso1 = unidades.find(u => u.unidad_id === PISO1_ID);
      let rentConsolidada = '';
      if (sumIngreso > 0 && unidadPiso1) {
        const rentCalc = calculateRentabilidadSync(unidadPiso1, { ingreso: sumIngreso, moneda: 'ARS' });
        if (rentCalc) rentConsolidada = rentCalc;
      }
      // Mantenemos resultado_id/estado/archivos si ya existían; sobrescribimos los montos.
      newTableData[PISO1_ID] = {
        ...newTableData[PISO1_ID],
        ingreso: sumIngreso || '',
        egreso: sumEgreso || '',
        fee: feeConsolidado,
        rentabilidad: rentConsolidada,
        moneda: 'ARS',
      };
    }
    
    setTableData(newTableData);
    setHasChanges(false);
  };

  // Versión sincrónica del cálculo de rentabilidad para buildTableData
  const calculateRentabilidadSync = (unidad, data) => {
    const ingreso = parseFloat(data.ingreso) || 0;
    if (ingreso <= 0) return null;
    
    const montoInversion = unidad.monto_inversion;
    if (!montoInversion || !montoInversion.monto || montoInversion.monto <= 0) return null;
    
    const valorOficina = montoInversion.monto;
    const monedaInversion = montoInversion.moneda || 'USD';
    const monedaIngreso = data.moneda || 'ARS';
    
    const usdArs = cotizacionPeriodo?.usd_ars || 0;
    
    // Si necesita conversión de moneda y no hay cotización, no calcular
    if (monedaIngreso !== monedaInversion && usdArs <= 0) return null;
    
    let ingresoUsd = ingreso;
    if (monedaIngreso === 'ARS' && usdArs > 0) {
      ingresoUsd = ingreso / usdArs;
    }
    
    let valorOficinaUsd = valorOficina;
    if (monedaInversion === 'ARS' && usdArs > 0) {
      valorOficinaUsd = valorOficina / usdArs;
    }
    
    if (valorOficinaUsd <= 0) return null;
    
    const rentabilidad = (ingresoUsd * 12) / valorOficinaUsd * 100;
    return Math.round(rentabilidad * 100) / 100;
  };

  // Auto-save individual unit to backend
  const autoSaveUnit = useCallback(async (unidadId, unitData) => {
    const unidad = unidades.find(u => u.unidad_id === unidadId);
    const tipoAdmin = unidad?.tipo_administracion || 'full';
    const ingreso = parseFloat(unitData.ingreso) || 0;
    const egreso = tipoAdmin === 'full' ? (parseFloat(unitData.egreso) || 0) : 0;
    if (ingreso <= 0 && egreso <= 0) return; // No guardar si no hay ni ingreso ni egreso
    
    try {
      const res = await resultadosApi.createBatch({
        edificio_id: edificioId,
        periodo: selectedPeriodo,
        resultados: [{
          unidad_id: unidadId,
          ingreso: { monto: ingreso, moneda: unitData.moneda || 'ARS' },
          egreso: { monto: tipoAdmin === 'full' ? (parseFloat(unitData.egreso) || 0) : 0, moneda: unitData.moneda || 'ARS' },
          fee_administracion: { monto: parseFloat(unitData.fee) || 0, moneda: unitData.moneda || 'ARS' },
          rentabilidad_anual_proyectada_pct: unitData.rentabilidad ? parseFloat(unitData.rentabilidad) : null,
          notas: unitData.notas || null,
          usa_manual: unitData.usa_manual === true || unitData.ingresoManual === true,
          ganancia_neta_manual: null,
        }]
      });
      // Update resultado_id in tableData if it was newly created
      const saved = res.data?.resultados?.[0];
      if (saved?.resultado_id) {
        setTableData(prev => ({
          ...prev,
          [unidadId]: {
            ...prev[unidadId], resultado_id: saved.resultado_id, estado: prev[unidadId]?.estado || 'borrador',
            // El backend calcula egreso (desde 8. Egresos) y fee: la pantalla muestra lo guardado.
            ...(saved.egreso !== undefined ? { egreso: saved.egreso } : {}),
            ...(saved.fee !== undefined ? { fee: saved.fee } : {}),
          }
        }));
        // Sincronizar el estado local `resultados` con lo recién guardado. Sin esto,
        // buildTableData (que depende de `resultados`) se re-ejecuta con datos viejos
        // (usa_manual=false) y vuelve a aplicar la precarga de Facturación, pisando en
        // pantalla la corrección manual del administrador.
        setResultados(prev => {
          const idx = prev.findIndex(r => r.unidad_id === unidadId && r.periodo === selectedPeriodo);
          const base = idx >= 0 ? prev[idx] : {};
          const merged = {
            ...base,
            resultado_id: saved.resultado_id,
            unidad_id: unidadId,
            periodo: selectedPeriodo,
            ingreso: { monto: ingreso, moneda: unitData.moneda || 'ARS' },
            egreso: { monto: saved.egreso ?? (tipoAdmin === 'full' ? (parseFloat(unitData.egreso) || 0) : 0), moneda: unitData.moneda || 'ARS' },
            fee_administracion: { monto: saved.fee ?? (parseFloat(unitData.fee) || 0), moneda: unitData.moneda || 'ARS' },
            usa_manual: unitData.usa_manual === true || unitData.ingresoManual === true,
            rentabilidad_anual_proyectada_pct: unitData.rentabilidad ? parseFloat(unitData.rentabilidad) : (base.rentabilidad_anual_proyectada_pct ?? null),
            notas: unitData.notas ?? base.notas ?? null,
            estado: base.estado || 'borrador',
          };
          if (idx >= 0) { const copy = [...prev]; copy[idx] = merged; return copy; }
          return [...prev, merged];
        });
      }
    } catch (err) {
      console.error('Error auto-saving unit:', err);
    }
  }, [edificioId, selectedPeriodo, unidades]);

  const handleTableChange = (unidadId, field, value) => {
    const currentData = tableData[unidadId] || {};
    const newData = {
      ...currentData,
      [field]: value
    };
    
    // Recalcular fee cuando cambia ingreso o egreso
    if (field === 'ingreso' || field === 'egreso') {
      const unidad = unidades.find(u => u.unidad_id === unidadId);
      if (unidad) {
        const tipoAdmin = unidad.tipo_administracion || 'full';
        const porcentaje = unidad.porcentaje_administracion ?? (tipoAdmin === 'full' ? 20 : 8);
        const ingreso = parseFloat(field === 'ingreso' ? value : newData.ingreso) || 0;
        const egreso = parseFloat(field === 'egreso' ? value : newData.egreso) || 0;
        
        if (ingreso > 0 && porcentaje > 0 && tipoAdmin === 'full') {
          const rentaBase = ingreso - egreso;
          newData.fee = rentaBase > 0 ? Math.round(rentaBase * (porcentaje / 100)) : 0;
        } else if (ingreso > 0 && porcentaje > 0) {
          newData.fee = Math.round(ingreso * (porcentaje / 100));
        }
      }
      if (field === 'ingreso') {
        newData.ingresoManual = true;
        newData.usa_manual = true;
      }
    }
    
    setTableData(prev => ({
      ...prev,
      [unidadId]: newData
    }));
    setHasChanges(true);

    // Auto-save con debounce de 800ms
    if (autoSaveTimers.current[unidadId]) {
      clearTimeout(autoSaveTimers.current[unidadId]);
    }
    autoSaveTimers.current[unidadId] = setTimeout(() => {
      autoSaveUnit(unidadId, newData);
      delete autoSaveTimers.current[unidadId];
    }, 800);
  };

  const calculateGanancia = (data, tipoAdministracion = 'full') => {
    const ingreso = parseFloat(data.ingreso) || 0;
    const egreso = tipoAdministracion === 'full' ? (parseFloat(data.egreso) || 0) : 0;
    const fee = parseFloat(data.fee) || 0;
    return ingreso - egreso - fee;
  };

  // Totales para la barra superior de Tab 9 (Liquidación) — replica el filtrado de TabLiquidacion
  // para mantener consistencia (Piso 1 sintético, oculta miembros, oculta cocheras asociadas).
  const totalesLiquidacion = React.useMemo(() => {
    if (!unidades || unidades.length === 0) return { ingreso: 0, egreso: 0, fee: 0, ganancia: 0 };
    const esTE = edificio?.modelo_administracion === 'gc_factura_y_cobra';
    const PISO1_ID = 'unit_piso1_te';
    const piso1MiembrosIds = esTE
      ? new Set(unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID).map(u => u.unidad_id))
      : new Set();
    const unidadesEnAdmin = esTE
      ? unidades.filter(u => {
          if (u.unidad_id === PISO1_ID) return true;
          if (u.en_administracion === false) return false;
          if (u.rol_unidad === 'componente') return false;
          if (piso1MiembrosIds.has(u.unidad_id)) return false;
          if (cocherasAsociadasMap[u.unidad_id]) return false;
          return true;
        })
      : unidades.filter(u => u.en_administracion !== false && !cocherasAsociadasMap[u.unidad_id]);
    let totIng = 0, totEgr = 0, totFee = 0, totGan = 0;
    unidadesEnAdmin.forEach(u => {
      const d = tableData[u.unidad_id] || {};
      const tipoAdmin = u.tipo_administracion || 'full';
      totIng += parseFloat(d.ingreso) || 0;
      totEgr += Math.abs(parseFloat(d.egreso) || 0);
      totFee += parseFloat(d.fee) || 0;
      totGan += calculateGanancia(d, tipoAdmin);
    });
    return { ingreso: totIng, egreso: totEgr, fee: totFee, ganancia: totGan };
  }, [tableData, unidades, edificio, cocherasAsociadasMap]);

  // Calcular rentabilidad automática: (Ingreso_USD × 12) / Valor_oficina_USD × 100
  const calculateRentabilidad = (unidad, data) => {
    const ingreso = parseFloat(data.ingreso) || 0;
    if (ingreso <= 0) return '';
    
    // Obtener valor de inversión de la unidad
    const montoInversion = unidad.monto_inversion;
    if (!montoInversion || !montoInversion.monto || montoInversion.monto <= 0) return '';
    
    const valorOficina = montoInversion.monto;
    const monedaInversion = montoInversion.moneda || 'USD';
    const monedaIngreso = data.moneda || 'ARS';
    
    // Cotización USD/ARS
    const usdArs = cotizacionPeriodo?.usd_ars || 1;
    
    // Convertir ingreso a USD
    let ingresoUsd = ingreso;
    if (monedaIngreso === 'ARS' && usdArs > 0) {
      ingresoUsd = ingreso / usdArs;
    }
    
    // Convertir valor oficina a USD
    let valorOficinaUsd = valorOficina;
    if (monedaInversion === 'ARS' && usdArs > 0) {
      valorOficinaUsd = valorOficina / usdArs;
    }
    
    if (valorOficinaUsd <= 0) return '';
    
    // Fórmula: (Ingreso_mensual_USD × 12) / Valor_oficina_USD × 100
    const rentabilidad = (ingresoUsd * 12) / valorOficinaUsd * 100;
    return Math.round(rentabilidad * 100) / 100; // Redondear a 2 decimales
  };

  // Actualizar rentabilidad cuando cambie el ingreso o la moneda
  const handleTableChangeWithRentabilidad = (unidadId, field, value) => {
    const unidad = unidades.find(u => u.unidad_id === unidadId);
    const currentData = tableData[unidadId] || {};
    
    // Actualizar el campo
    const newData = { ...currentData, [field]: value };
    
    // Si cambió el ingreso o la moneda, recalcular rentabilidad
    if ((field === 'ingreso' || field === 'moneda') && unidad) {
      const nuevaRentabilidad = calculateRentabilidad(unidad, newData);
      newData.rentabilidad = nuevaRentabilidad;
    }
    
    // Si cambió el ingreso o egreso, recalcular el fee automáticamente
    if ((field === 'ingreso' || field === 'egreso') && unidad) {
      const tipoAdmin = unidad.tipo_administracion || 'full';
      const porcentaje = unidad.porcentaje_administracion ?? (tipoAdmin === 'full' ? 20 : 8);
      const ingreso = parseFloat(field === 'ingreso' ? value : newData.ingreso) || 0;
      const egreso = parseFloat(field === 'egreso' ? value : newData.egreso) || 0;
      
      if (ingreso > 0 && porcentaje > 0) {
        if (tipoAdmin === 'full') {
          const rentaBase = ingreso - egreso;
          // Si la renta es negativa, el fee es 0
          newData.fee = rentaBase > 0 ? Math.round(rentaBase * (porcentaje / 100)) : 0;
        } else {
          newData.fee = Math.round(ingreso * (porcentaje / 100));
        }
      }
    }
    
    setTableData(prev => ({
      ...prev,
      [unidadId]: newData
    }));
    setHasChanges(true);
  };

  const handleSaveBatch = async () => {
    setSavingBatch(true);
    
    try {
      // Filter only rows that have data
      const resultadosToSave = [];
      
      Object.entries(tableData).forEach(([unidadId, data]) => {
        // Find the unidad to get tipo_administracion
        const unidad = unidades.find(u => u.unidad_id === unidadId);
        const tipoAdmin = unidad?.tipo_administracion || 'full';
        
        // Only include if at least ingreso is filled
        if (data.ingreso !== '' && data.ingreso !== null) {
          resultadosToSave.push({
            unidad_id: unidadId,
            ingreso: { monto: parseFloat(data.ingreso) || 0, moneda: data.moneda },
            egreso: { monto: tipoAdmin === 'full' ? (parseFloat(data.egreso) || 0) : 0, moneda: data.moneda },
            fee_administracion: { monto: parseFloat(data.fee) || 0, moneda: data.moneda },
            rentabilidad_anual_proyectada_pct: data.rentabilidad ? parseFloat(data.rentabilidad) : null,
            notas: data.notas || null,
            usa_manual: data.usa_manual === true || data.ingresoManual === true,
            ganancia_neta_manual: null,
          });
        }
      });
      
      if (resultadosToSave.length === 0) {
        toast.error('No hay datos para guardar');
        setSavingBatch(false);
        return;
      }
      
      await resultadosApi.createBatch({
        edificio_id: edificioId,
        periodo: selectedPeriodo,
        resultados: resultadosToSave
      });
      
      toast.success(`${resultadosToSave.length} resultados guardados`);
      setHasChanges(false);
      fetchData(); // Refresh to get updated data
      
    } catch (err) {
      console.error('Error saving batch:', err);
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingBatch(false);
    }
  };

  const handlePublicar = async (resultadoId) => {
    try {
      await resultadosApi.publicar(resultadoId);
      toast.success('Resultado publicado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Faltan archivos obligatorios');
    }
  };

  const handleCambiarEstado = async (resultadoId, unidadId, nuevoEstado) => {
    try {
      const r = await resultadosApi.cambiarEstado(resultadoId, nuevoEstado);
      const aviso = r?.data?.aviso;
      setTableData(prev => ({
        ...prev,
        [unidadId]: {
          ...prev[unidadId], estado: nuevoEstado,
          ...(aviso?.enviados?.length ? { renta_email_enviada_at: aviso.enviada_at, renta_email_error: null } : {}),
          ...(aviso?.errores?.length && !aviso?.enviados?.length ? { renta_email_error: aviso.errores.join(' · ') } : {}),
        }
      }));
      if (aviso?.enviados?.length) toast.success(`Aviso de renta enviado a ${aviso.enviados.join(', ')}`);
      if (aviso?.errores?.length) toast.warning(`Aviso no enviado: ${aviso.errores.join(' · ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleCambiarEstadoGrupo = async (grupoId, nuevoEstado) => {
    try {
      const r = await resultadosApi.cambiarEstadoGrupo(grupoId, nuevoEstado, selectedPeriodo);
      toast.success(`Grupo actualizado a ${nuevoEstado} (${r.data?.afectados ?? 0} unidades)`);
      if (r.data?.aviso?.enviados?.length) toast.success(`Aviso de renta enviado a ${r.data.aviso.enviados.join(', ')}`);
      if (r.data?.aviso?.errores?.length) toast.warning(`Aviso no enviado: ${r.data.aviso.errores.join(' · ')}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado del grupo');
    }
  };

  const handleFileUpload = async (resultadoId, tipoArchivo, file) => {
    setUploadingFile(`${resultadoId}-${tipoArchivo}`);
    try {
      await archivosApi.uploadResultado(resultadoId, tipoArchivo, file);
      toast.success('Archivo subido');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploadingFile(null);
    }
  };

  const navigatePeriodo = (direction) => {
    // Incluye el mes siguiente durante los últimos 5 días del mes (igual que el
    // desplegable de Facturación) para poder avanzar a septiembre a fin de agosto.
    const periods = getFacturacionPeriods(24);
    const currentIndex = periods.indexOf(selectedPeriodo);
    if (direction === 'prev' && currentIndex < periods.length - 1) {
      setSelectedPeriodo(periods[currentIndex + 1]);
    } else if (direction === 'next' && currentIndex > 0) {
      setSelectedPeriodo(periods[currentIndex - 1]);
    }
  };

  // ============ EDIFICIO HANDLERS ============
  const handleEditEdificio = () => {
    setEdificioForm({
      nombre: edificio.nombre,
      denominacion: edificio.denominacion || '',
      direccion: edificio.direccion,
      descripcion: edificio.descripcion || '',
      estado: edificio.estado,
      valor_cochera_estandar: edificio.valor_cochera_estandar || null,
      monto_cowork_virtual: edificio.monto_cowork_virtual ?? null,
      monto_cowork_full: edificio.monto_cowork_full ?? null,
      padron_municipal_unificado: edificio.padron_municipal_unificado || '',
      padron_rentas_unificado: edificio.padron_rentas_unificado || '',
      nomenclatura_catastral_unificada: edificio.nomenclatura_catastral_unificada || '',
      servicios_areas_comunes: edificio.servicios_areas_comunes || [],
    });
    setIsEditEdificioOpen(true);
  };

  const handleSaveEdificio = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await edificiosApi.update(edificioId, edificioForm);
      toast.success('Edificio actualizado');
      setIsEditEdificioOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ============ UNIDAD HANDLERS ============
  const handleOpenCreateUnidad = () => {
    setSelectedUnidad(null);
    setUnidadForm(initialUnidadForm);
    setIsUnidadDialogOpen(true);
  };

  const handleOpenEditUnidad = (unidad) => {
    setSelectedUnidad(unidad);
    setUnidadTab('general'); // Reset a la pestaña general
    setUnidadForm({
      nombre_unidad: unidad.nombre_unidad,
      tipo: unidad.tipo,
      m2_cubiertos: unidad.m2_cubiertos || '',
      m2_semicubiertos: unidad.m2_semicubiertos || '',
      m2_comunes: unidad.m2_comunes || '',
      m2_descubiertos: unidad.m2_descubiertos || '',
      m2_balcon: unidad.m2_balcon || '',
      m2_totales: unidad.m2_totales || '',
      m2_alquiler: unidad.m2_alquiler || '',
      m2_expensas: unidad.m2_expensas || '',
      m2_cubiertos_total: unidad.m2_cubiertos_total || '',
      m2_cochera: unidad.m2_cochera || '',
      nomenclatura_catastral: unidad.nomenclatura_catastral || '',
      padron_territorial: unidad.padron_territorial || '',
      nic_energia_electrica: unidad.nic_energia_electrica || '',
      nic_energia_no_aplica: unidad.nic_energia_no_aplica || false,
      nic_agua: unidad.nic_agua || '',
      nic_agua_no_aplica: unidad.nic_agua_no_aplica || false,
      tasa_municipal: unidad.tasa_municipal || '',
      tasa_municipal_no_aplica: unidad.tasa_municipal_no_aplica || false,
      tasa_comercial: unidad.tasa_comercial || '',
      tasa_comercial_no_aplica: unidad.tasa_comercial_no_aplica || false,
      // Foresta-only: Cochera
      tiene_cochera: unidad.tiene_cochera || false,
      cochera_numero: unidad.cochera_numero || '',
      cochera_observaciones: unidad.cochera_observaciones || '',
      // Foresta-only: Amoblamiento
      tiene_amoblamiento: unidad.tiene_amoblamiento || false,
      amoblamiento_detalle: {
        aires: unidad.amoblamiento_detalle?.aires || false,
        cortinas: unidad.amoblamiento_detalle?.cortinas || false,
        muebles: unidad.amoblamiento_detalle?.muebles || false,
        sillas: unidad.amoblamiento_detalle?.sillas || false,
        pizarra: unidad.amoblamiento_detalle?.pizarra || false,
      },
      // Inversión
      fecha_adquisicion: unidad.fecha_adquisicion || '',
      monto_inversion: unidad.monto_inversion
        ? { monto: unidad.monto_inversion.monto ?? null, moneda: unidad.monto_inversion.moneda || 'USD', iva: unidad.monto_inversion.iva ?? null }
        : { monto: null, moneda: 'USD', iva: null },
      // Capacidad
      capacidad_personas: unidad.capacidad_personas ?? '',
      escritorios_cantidad: unidad.escritorios_cantidad ?? '',
      sillas_cantidad: unidad.sillas_cantidad ?? '',
      descripcion_equipamiento: unidad.descripcion_equipamiento || '',
    });
    setIsUnidadDialogOpen(true);
  };

  const handleSaveUnidad = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        edificio_id: edificioId,
        nombre_unidad: unidadForm.nombre_unidad,
        tipo: unidadForm.tipo,
        m2_cubiertos: parseFloat(unidadForm.m2_cubiertos) || 0,
        m2_semicubiertos: parseFloat(unidadForm.m2_semicubiertos) || 0,
        // m2_comunes (legacy) se preserva sin tocar - no se envía desde el modal
        m2_descubiertos: parseFloat(unidadForm.m2_descubiertos) || 0,
        m2_balcon: parseFloat(unidadForm.m2_balcon) || 0,
        m2_totales: parseFloat(unidadForm.m2_totales) || 0,
        m2_alquiler: parseFloat(unidadForm.m2_alquiler) || 0,
        m2_expensas: parseFloat(unidadForm.m2_expensas) || 0,
        m2_cubiertos_total: parseFloat(unidadForm.m2_cubiertos_total) || 0,
        m2_cochera: parseFloat(unidadForm.m2_cochera) || 0,
        nomenclatura_catastral: unidadForm.nomenclatura_catastral || null,
        nic_energia_electrica: unidadForm.nic_energia_electrica || null,
        nic_energia_no_aplica: unidadForm.nic_energia_no_aplica || false,
        nic_agua: unidadForm.nic_agua || null,
        nic_agua_no_aplica: unidadForm.nic_agua_no_aplica || false,
        tasa_municipal: unidadForm.tasa_municipal || null,
        tasa_municipal_no_aplica: unidadForm.tasa_municipal_no_aplica || false,
        tasa_comercial: unidadForm.tasa_comercial || null,
        tasa_comercial_no_aplica: unidadForm.tasa_comercial_no_aplica || false,
        // Foresta-only: Cochera
        tiene_cochera: unidadForm.tiene_cochera || false,
        cochera_numero: unidadForm.cochera_numero || null,
        cochera_observaciones: unidadForm.cochera_observaciones || null,
        // Foresta-only: Amoblamiento
        tiene_amoblamiento: unidadForm.tiene_amoblamiento || false,
        amoblamiento_detalle: unidadForm.amoblamiento_detalle || null,
      };

      // Inversión (solapa Inversión) - solo enviar si el usuario tocó algo
      if (unidadForm.fecha_adquisicion !== undefined) {
        submitData.fecha_adquisicion = unidadForm.fecha_adquisicion || null;
      }
      const mi = unidadForm.monto_inversion || {};
      if (mi.monto != null || mi.iva != null) {
        submitData.monto_inversion = {
          monto: mi.monto != null ? Number(mi.monto) : 0,
          moneda: mi.moneda || 'USD',
          iva: mi.iva != null ? Number(mi.iva) : null,
        };
      }

      // Capacidad (solapa Capacidad) — enviar siempre (permite limpiar a 0/null)
      submitData.capacidad_personas = unidadForm.capacidad_personas === '' || unidadForm.capacidad_personas == null ? null : parseInt(unidadForm.capacidad_personas);
      submitData.escritorios_cantidad = unidadForm.escritorios_cantidad === '' || unidadForm.escritorios_cantidad == null ? null : parseInt(unidadForm.escritorios_cantidad);
      submitData.sillas_cantidad = unidadForm.sillas_cantidad === '' || unidadForm.sillas_cantidad == null ? null : parseInt(unidadForm.sillas_cantidad);
      submitData.descripcion_equipamiento = unidadForm.descripcion_equipamiento || null;

      if (selectedUnidad) {
        await unidadesApi.update(selectedUnidad.unidad_id, submitData);
        toast.success('Unidad actualizada');
      } else {
        await unidadesApi.create(submitData);
        toast.success('Unidad creada');
      }
      setIsUnidadDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ============ PROPIETARIO HANDLERS ============
  const handleRemovePropietario = async (unidad, propietarioId) => {
    // Quita UN propietario. Preserva proporciones relativas de los que quedan.
    // No borra el propietario global; solo quita la relación.
    const original = unidad.propietarios || [];
    const lista = original.filter(p => p.propietario_id !== propietarioId);
    try {
      let body;
      if (lista.length === 0) {
        body = { propietarios: [] };
      } else if (lista.length === 1) {
        body = {
          propietarios: [{
            propietario_id: lista[0].propietario_id,
            porcentaje: 100,
            fecha_adquisicion: lista[0].fecha_adquisicion || null,
            monto_inversion: lista[0].monto_inversion || null,
            monto_total_compra: lista[0].monto_total_compra || null,
          }],
        };
      } else {
        // Preservar proporciones relativas, normalizadas a 100.
        const sumaRestante = lista.reduce((acc, p) => acc + Number(p.porcentaje || 0), 0);
        if (sumaRestante <= 0) {
          toast.error('No se puede recalcular porcentajes (la suma restante es 0). Editá manualmente desde el modal Propietario.');
          return;
        }
        const ajustada = lista.map(p => ({
          propietario_id: p.propietario_id,
          porcentaje: +((Number(p.porcentaje || 0) * 100) / sumaRestante).toFixed(4),
          fecha_adquisicion: p.fecha_adquisicion || null,
          monto_inversion: p.monto_inversion || null,
          monto_total_compra: p.monto_total_compra || null,
        }));
        // Ajuste fino: la suma puede diferir por redondeo. Lo absorbe el último.
        const suma = ajustada.reduce((acc, p) => acc + p.porcentaje, 0);
        if (Math.abs(suma - 100) > 0.0001) {
          ajustada[ajustada.length - 1].porcentaje = +(ajustada[ajustada.length - 1].porcentaje + (100 - suma)).toFixed(4);
        }
        body = { propietarios: ajustada };
      }
      await unidadesApi.updatePropietario(unidad.unidad_id, body);
      const msg = lista.length === 0
        ? 'Propietario quitado'
        : lista.length === 1
          ? 'Propietario quitado · el restante queda con 100%'
          : 'Propietario quitado · porcentajes ajustados manteniendo proporciones';
      toast.success(msg);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo quitar el propietario');
    }
  };

  const handleOpenPropietario = (unidad) => {
    setSelectedUnidad(unidad);
    // Cargar múltiples propietarios si existen
    const propietariosExistentes = unidad.propietarios || [];
    
    if (propietariosExistentes.length > 0) {
      // Formato nuevo: múltiples propietarios
      setPropietarioForm({
        propietario_id: propietariosExistentes[0]?.propietario_id || '',
        propietarios: propietariosExistentes.map(p => ({
          propietario_id: p.propietario_id,
          porcentaje: p.porcentaje || 100,
          fecha_adquisicion: p.fecha_adquisicion || '',
          monto_inversion: p.monto_inversion || null,
        })),
        fecha_adquisicion: unidad.fecha_adquisicion || '',
        monto_inversion: unidad.monto_inversion || { monto: '', moneda: 'USD' },
      });
      setEditingPropietario(false); // No mostrar lista si ya tiene propietarios
    } else if (unidad.propietario_id) {
      // Legacy: un solo propietario sin array
      const propInfo = propietarios.find(p => p.propietario_id === unidad.propietario_id);
      setPropietarioForm({
        propietario_id: unidad.propietario_id,
        propietarios: [{
          propietario_id: unidad.propietario_id,
          porcentaje: 100,
        }],
        fecha_adquisicion: unidad.fecha_adquisicion || '',
        monto_inversion: unidad.monto_inversion || { monto: '', moneda: 'USD' },
      });
      setEditingPropietario(false);
    } else {
      // Sin propietario
      setPropietarioForm({
        propietario_id: '',
        propietarios: [],
        fecha_adquisicion: unidad.fecha_adquisicion || '',
        monto_inversion: unidad.monto_inversion || { monto: '', moneda: 'USD' },
      });
      setEditingPropietario(true); // Mostrar lista para seleccionar
    }
    setIsPropietarioDialogOpen(true);
  };

  const handleSavePropietario = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let submitData;
      
      // Si hay múltiples propietarios configurados, enviar ese formato
      if (propietarioForm.propietarios && propietarioForm.propietarios.length > 0) {
        // Validar que los porcentajes sumen 100
        const totalPorcentaje = propietarioForm.propietarios.reduce((sum, p) => sum + (parseFloat(p.porcentaje) || 0), 0);
        if (Math.abs(totalPorcentaje - 100) > 0.01) {
          toast.error(`Los porcentajes deben sumar 100%. Suma actual: ${totalPorcentaje.toFixed(2)}%`);
          setSaving(false);
          return;
        }
        
        submitData = {
          propietarios: propietarioForm.propietarios.map(p => ({
            propietario_id: p.propietario_id,
            porcentaje: parseFloat(p.porcentaje) || 0,
            fecha_adquisicion: p.fecha_adquisicion || propietarioForm.fecha_adquisicion || null,
            monto_inversion: p.monto_inversion?.monto ? {
              monto: parseFloat(p.monto_inversion.monto),
              moneda: p.monto_inversion.moneda || 'USD',
              iva: parseFloat(p.monto_inversion.iva) || 0,
            } : null,
          })),
          fecha_adquisicion: propietarioForm.fecha_adquisicion || null,
          monto_inversion: propietarioForm.monto_inversion?.monto ? {
            monto: parseFloat(propietarioForm.monto_inversion.monto),
            moneda: propietarioForm.monto_inversion.moneda,
            iva: parseFloat(propietarioForm.monto_inversion.iva) || 0,
          } : null,
        };
      } else {
        // Formato legacy: un solo propietario
        submitData = {
          propietario_id: propietarioForm.propietario_id || null,
          fecha_adquisicion: propietarioForm.fecha_adquisicion || null,
          monto_inversion: propietarioForm.monto_inversion?.monto ? {
            monto: parseFloat(propietarioForm.monto_inversion.monto),
            moneda: propietarioForm.monto_inversion.moneda,
            iva: parseFloat(propietarioForm.monto_inversion.iva) || 0,
          } : null,
        };
      }

      await unidadesApi.updatePropietario(selectedUnidad.unidad_id, submitData);
      toast.success('Propietario(s) asignado(s)');
      setIsPropietarioDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Handler para crear nuevo propietario
  const handleCreateNuevoPropietario = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const f = nuevoPropietarioForm;
      const esHumana = f.tipo === 'persona_humana' || f.tipo === 'persona_fisica';
      // nombre_razon_social derivado para retrocompatibilidad (lo recalcula también el backend).
      const nrs = esHumana
        ? `${(f.nombre || '').trim()} ${(f.apellido || '').trim()}`.trim()
        : (f.razon_social || '').trim();
      const response = await propietariosApi.create({
        // Titular
        tipo: f.tipo,
        nombre_razon_social: nrs,
        nombre: esHumana ? (f.nombre || null) : null,
        apellido: esHumana ? (f.apellido || null) : null,
        razon_social: esHumana ? null : (f.razon_social || null),
        dni: f.dni || null,
        email_legal: f.email_legal || null,
        direccion: f.direccion || null,
        notas: f.notas || null,
        // Contacto
        email: f.email,
        whatsapp: f.whatsapp || null,
        preferencia_contacto: f.preferencia_contacto || 'email',
        contact_is_same_as_owner: f.contact_is_same_as_owner !== false,
        contact_person_first_name: (f.contact_is_same_as_owner === false ? (f.contact_person_first_name || null) : null),
        contact_person_last_name:  (f.contact_is_same_as_owner === false ? (f.contact_person_last_name  || null) : null),
        // Fiscal
        razon_social_fiscal: f.razon_social_fiscal || null,
        cuit_cuil: f.cuit_cuil || null,
        condicion_iva: f.condicion_iva || null,
        domicilio_fiscal: f.domicilio_fiscal || null,
        tipo_factura: f.tipo_factura || null,
        email_facturacion: f.email_facturacion || null,
        // Bancario
        cbu: f.cbu || null,
        alias_cbu: f.alias_cbu || null,
        banco: f.banco || null,
        titular_cuenta: f.titular_cuenta || null,
      });
      toast.success('Propietario creado');
      setPropietarioForm({ ...propietarioForm, propietario_id: response.propietario_id });
      setIsNuevoPropietarioOpen(false);
      setNuevoPropietarioTab('titular');
      setNuevoPropietarioForm({
        tipo: 'persona_humana',
        nombre: '', apellido: '', razon_social: '', nombre_razon_social: '',
        dni: '', email_legal: '', direccion: '', notas: '',
        contact_is_same_as_owner: true, contact_person_first_name: '', contact_person_last_name: '',
        email: '', whatsapp: '', preferencia_contacto: 'email',
        razon_social_fiscal: '', cuit_cuil: '', condicion_iva: '', domicilio_fiscal: '', email_facturacion: '', tipo_factura: '',
        cbu: '', alias_cbu: '', banco: '', titular_cuenta: '',
        telefono: '', localidad: '', provincia: '', codigo_postal: '',
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear propietario');
    } finally {
      setSaving(false);
    }
  };

  // Handler para abrir modal de cambiar propietario (desde tabla de Unidades)
  const handleOpenCambiarPropietario = (unidad) => {
    setSelectedUnidad(unidad);
    setCambiarPropietarioForm({
      propietario_id: '',
      fecha_adquisicion: '',
      monto_inversion: { monto: '', moneda: 'USD' }
    });
    setPropietarioSearch('');
    setIsCambiarPropietarioOpen(true);
  };

  // Handler para guardar cambio de propietario
  const handleSaveCambiarPropietario = async (e) => {
    e.preventDefault();
    if (!cambiarPropietarioForm.propietario_id) {
      toast.error('Selecciona un propietario');
      return;
    }
    setSaving(true);
    try {
      const submitData = {
        propietario_id: cambiarPropietarioForm.propietario_id,
        fecha_adquisicion: cambiarPropietarioForm.fecha_adquisicion || null,
        monto_inversion: cambiarPropietarioForm.monto_inversion?.monto ? {
          monto: parseFloat(cambiarPropietarioForm.monto_inversion.monto),
          moneda: cambiarPropietarioForm.monto_inversion.moneda,
        } : null,
      };

      await unidadesApi.updatePropietario(selectedUnidad.unidad_id, submitData);
      toast.success('Propietario cambiado. El anterior fue guardado en históricos.');
      setIsCambiarPropietarioOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar propietario');
    } finally {
      setSaving(false);
    }
  };

  // ============ ALQUILER HANDLERS ============
  const handleOpenAlquiler = (unidad) => {
    setSelectedUnidad(unidad);
    const ar = unidad.alquiler_resumen || {};
    const tipoAdmin = unidad.tipo_administracion || 'full';
    // Calcular el porcentaje por defecto según el tipo si no está definido
    const defaultPorcentaje = tipoAdmin === 'full' ? 20 : 8;
    setAlquilerForm({
      en_administracion: unidad.en_administracion || false,
      estado_alquiler: unidad.estado_alquiler || 'vacante',
      tipo_administracion: tipoAdmin,
      porcentaje_administracion: unidad.porcentaje_administracion ?? defaultPorcentaje,
      inquilino: ar.inquilino || '',
      fecha_inicio: ar.fecha_inicio || '',
      fecha_fin: ar.fecha_fin || '',
      monto_alquiler_inicio: ar.monto_alquiler_inicio || { monto: '', moneda: 'ARS' },
      alquiler_actual: ar.alquiler_actual || { monto: '', moneda: 'ARS' },
      indice_actualizacion: ar.indice_actualizacion || { tipo: 'ipc', valor: '', frecuencia: 'trimestral' },
      contrato_nombre: ar.contrato_nombre || null,
      contrato_archivo_id: ar.contrato_archivo_id || null,
    });
    setContratoFile(null);
    setIsAlquilerDialogOpen(true);
  };

  const handleSaveAlquiler = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = {
        en_administracion: alquilerForm.en_administracion,
        estado_alquiler: alquilerForm.estado_alquiler,
        tipo_administracion: alquilerForm.tipo_administracion,
        porcentaje_administracion: alquilerForm.porcentaje_administracion,
        inquilino: alquilerForm.inquilino || null,
        fecha_inicio: alquilerForm.fecha_inicio || null,
        fecha_fin: alquilerForm.fecha_fin || null,
        monto_alquiler_inicio: alquilerForm.monto_alquiler_inicio?.monto ? {
          monto: parseFloat(alquilerForm.monto_alquiler_inicio.monto),
          moneda: alquilerForm.monto_alquiler_inicio.moneda,
        } : null,
        alquiler_actual: alquilerForm.alquiler_actual?.monto ? {
          monto: parseFloat(alquilerForm.alquiler_actual.monto),
          moneda: alquilerForm.alquiler_actual.moneda,
        } : null,
        indice_actualizacion: alquilerForm.en_administracion ? {
          tipo: alquilerForm.indice_actualizacion.tipo,
          valor: alquilerForm.indice_actualizacion.tipo === 'porcentaje' 
            ? parseFloat(alquilerForm.indice_actualizacion.valor) || null 
            : null,
          frecuencia: alquilerForm.indice_actualizacion.frecuencia,
        } : null,
      };

      await unidadesApi.updateAlquiler(selectedUnidad.unidad_id, submitData);
      
      // Upload contrato if selected
      if (contratoFile) {
        await unidadesApi.uploadContrato(selectedUnidad.unidad_id, contratoFile);
      }
      
      toast.success('Configuración de alquiler guardada');
      setIsAlquilerDialogOpen(false);
      setContratoFile(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadContrato = async (file) => {
    if (!selectedUnidad) return;
    setUploadingContrato(true);
    setAnalizandoContrato(true);
    
    try {
      // First, analyze the contract with AI
      toast.info('Analizando contrato con IA...');
      const analisis = await unidadesApi.analizarContrato(selectedUnidad.unidad_id, file);
      
      if (analisis.data.mensaje && analisis.data.inquilino) {
        // Auto-fill the form with extracted data
        setAlquilerForm(prev => ({
          ...prev,
          en_administracion: true,
          inquilino: analisis.data.inquilino || prev.inquilino,
          fecha_inicio: analisis.data.fecha_inicio || prev.fecha_inicio,
          fecha_fin: analisis.data.fecha_fin || prev.fecha_fin,
          monto_alquiler_inicio: analisis.data.monto_alquiler_inicio 
            ? { monto: analisis.data.monto_alquiler_inicio, moneda: analisis.data.moneda || 'ARS' }
            : prev.monto_alquiler_inicio,
          alquiler_actual: analisis.data.monto_actual
            ? { monto: analisis.data.monto_actual, moneda: analisis.data.moneda || 'ARS' }
            : (analisis.data.monto_alquiler_inicio 
              ? { monto: analisis.data.monto_alquiler_inicio, moneda: analisis.data.moneda || 'ARS' }
              : prev.alquiler_actual),
          indice_actualizacion: {
            tipo: analisis.data.indice_tipo || 'ipc',
            valor: prev.indice_actualizacion?.valor || '',
            frecuencia: analisis.data.indice_frecuencia || 'trimestral'
          }
        }));
        
        toast.success(`✨ Contrato analizado (confianza: ${analisis.data.confianza || 'media'})`);
      }
      
      // Then upload the contract
      await unidadesApi.uploadContrato(selectedUnidad.unidad_id, file);
      
      // Update local state
      setAlquilerForm(prev => ({
        ...prev,
        contrato_nombre: file.name,
        contrato_archivo_id: 'uploaded'
      }));
      setContratoFile(null);
      fetchData();
      
    } catch (err) {
      console.error('Error:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar contrato');
    } finally {
      setUploadingContrato(false);
      setAnalizandoContrato(false);
    }
  };

  const handleDeleteContrato = async () => {
    if (!selectedUnidad) return;
    try {
      await unidadesApi.deleteContrato(selectedUnidad.unidad_id);
      toast.success('Contrato eliminado');
      setAlquilerForm(prev => ({
        ...prev,
        contrato_nombre: null,
        contrato_archivo_id: null
      }));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar contrato');
    }
  };

  const handleDeleteUnidad = async () => {
    if (!selectedUnidad) return;
    const unidadId = selectedUnidad.unidad_id;
    // Snapshot para restaurar si falla
    const prevUnidades = unidades;
    const prevGrupos = grupos;
    // Optimistic UI: cerramos modal y removemos la fila al instante
    setIsDeleteUnidadOpen(false);
    setSelectedUnidad(null);
    setUnidades(prev => prev.filter(u => u.unidad_id !== unidadId));
    setGrupos(prev => prev.map(g => ({
      ...g,
      unidad_ids: (g.unidad_ids || []).filter(id => id !== unidadId)
    })));
    try {
      await unidadesApi.delete(unidadId);
      toast.success('Unidad eliminada');
    } catch (err) {
      // Restaurar fila y grupos
      setUnidades(prevUnidades);
      setGrupos(prevGrupos);
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleOpenMasivo = () => {
    setMasivoData({ propietario_id: '', tipo: 'oficina', nombres: '' });
    setIsMasivoDialogOpen(true);
  };

  const handleSaveMasivo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const nombres = masivoData.nombres.split('\n').filter(n => n.trim());
      if (nombres.length === 0) {
        toast.error('Ingresa al menos un nombre');
        setSaving(false);
        return;
      }
      await unidadesApi.createMasivo({
        edificio_id: edificioId,
        propietario_id: masivoData.propietario_id,
        tipo: masivoData.tipo,
        nombres,
      });
      toast.success(`${nombres.length} unidades creadas`);
      setIsMasivoDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  // Alta Rápida Handlers
  const handleOpenAltaRapida = () => {
    setAltaRapidaData([{ nombre_unidad: '', tipo: 'oficina' }]);
    setIsAltaRapidaDialogOpen(true);
  };

  // ============ GRUPOS DE UNIDADES HANDLERS ============
  
  // Handler para cambiar el orden de una fila (unidad o grupo)
  const handleCambiarOrden = async (tipo, id, nuevoOrden) => {
    try {
      const ordenNum = parseInt(nuevoOrden, 10);
      if (isNaN(ordenNum) || ordenNum < 1) return;
      
      if (tipo === 'grupo') {
        await gruposUnidadesApi.update(id, { orden: ordenNum });
      } else {
        await unidadesApi.update(id, { orden: ordenNum });
      }
      fetchData(); // Recargar datos para ver el nuevo orden
    } catch (err) {
      console.error('Error al cambiar orden:', err);
      toast.error('Error al cambiar el orden');
    }
  };
  
  const handleOpenGrupo = () => {
    setGrupoForm({ nombre: '', unidad_ids: [], mismo_propietario: false });
    setEditingGrupoId(null);
    setIsGrupoDialogOpen(true);
  };

  const handleOpenEditGrupo = (grupo) => {
    setGrupoForm({ nombre: grupo.nombre, unidad_ids: [...grupo.unidad_ids], mismo_propietario: grupo.mismo_propietario || false });
    setEditingGrupoId(grupo.grupo_id);
    setIsGrupoDialogOpen(true);
  };

  const handleSaveGrupo = async (e) => {
    e.preventDefault();
    if (!grupoForm.nombre.trim() || grupoForm.unidad_ids.length < 2) return;
    setSavingGrupo(true);
    try {
      if (editingGrupoId) {
        // Modo edición
        await gruposUnidadesApi.update(editingGrupoId, {
          nombre: grupoForm.nombre,
          unidad_ids: grupoForm.unidad_ids,
          mismo_propietario: grupoForm.mismo_propietario,
        });
        toast.success('Grupo actualizado');
      } else {
        // Modo creación
        await gruposUnidadesApi.create({
          edificio_id: edificioId,
          nombre: grupoForm.nombre,
          unidad_ids: grupoForm.unidad_ids,
          mismo_propietario: grupoForm.mismo_propietario,
        });
        toast.success('Grupo creado');
      }
      setIsGrupoDialogOpen(false);
      setEditingGrupoId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (editingGrupoId ? 'Error al actualizar grupo' : 'Error al crear grupo'));
    } finally {
      setSavingGrupo(false);
    }
  };

  const handleDeleteGrupo = async () => {
    if (!deleteGrupoTarget) return;
    try {
      await gruposUnidadesApi.delete(deleteGrupoTarget);
      toast.success('Grupo eliminado');
      setDeleteGrupoTarget(null);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar grupo');
    }
  };

  const toggleGrupoExpanded = (grupoId) => {
    setExpandedGrupos(prev => ({ ...prev, [grupoId]: !prev[grupoId] }));
  };

  // Get unit IDs that are already in a group
  const unidadesEnGrupo = new Set(grupos.flatMap(g => g.unidad_ids));
  const getGrupoDe = (unidadId) => grupos.find(g => g.unidad_ids.includes(unidadId));

  // ============ ALTA ADMINISTRACIÓN HANDLERS ============
  const handleOpenAltaAdmin = (preselect = {}) => {
    setAltaAdminForm({
      unidad_id: preselect.unidad_id || '',
      servicio_busqueda_activo: !!preselect.servicio_busqueda_activo,
      servicio_admin_activo: !!preselect.servicio_admin_activo,
      tipo_administracion: 'full',
      porcentaje_administracion: 20,
      contrato_file: null,
      contrato_tipo: 'mismo',
      alcance_grupo: 'solo_unidad',
      contrato_busqueda_file: null,
    });
    setIsAltaAdminOpen(true);
  };

  const handleContratoBusquedaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingContratoBusqueda(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', 'contrato_busqueda_inquilino');
      const res = await archivosApi.upload(formData);
      setAltaAdminForm(prev => ({
        ...prev,
        contrato_busqueda_file: {
          url: res.data.url,
          nombre: file.name,
          uploaded_at: new Date().toISOString()
        }
      }));
      toast.success('Contrato de búsqueda subido');
    } catch (err) {
      console.error('Error uploading contrato busqueda:', err);
      toast.error('Error al subir el contrato de búsqueda');
    } finally {
      setUploadingContratoBusqueda(false);
    }
  };

  const handleUploadContratoBusqueda = async (unidadId, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', 'contrato_busqueda_inquilino');
      const res = await archivosApi.upload(formData);
      await unidadesApi.update(unidadId, {
        contrato_busqueda_inquilino: {
          url: res.data.url,
          nombre: file.name,
          uploaded_at: new Date().toISOString()
        }
      });
      toast.success('Contrato de búsqueda subido');
      fetchData();
    } catch (err) {
      console.error('Error uploading contrato busqueda:', err);
      toast.error('Error al subir el contrato de búsqueda');
    }
  };

  const handleBajaServicioBusqueda = async (unidadId) => {
    // Actualización LOCAL optimista para no recargar la pantalla ni resetear el buscador.
    const snapshot = unidades.find(u => u.unidad_id === unidadId);
    setUnidades(prev => prev.map(u => u.unidad_id === unidadId ? { ...u, servicio_busqueda_inquilino_activo: false } : u));
    try {
      await unidadesApi.update(unidadId, {
        servicio_busqueda_inquilino_activo: false,
      });
      toast.success('Servicio de búsqueda dado de baja');
    } catch (err) {
      // Rollback
      setUnidades(prev => prev.map(u => u.unidad_id === unidadId ? { ...u, servicio_busqueda_inquilino_activo: !!snapshot?.servicio_busqueda_inquilino_activo } : u));
      toast.error('Error al dar de baja servicio de búsqueda');
    }
  };

  const handleActivarServicioBusqueda = async (unidadId) => {
    // Actualización LOCAL optimista para no recargar la pantalla ni resetear el buscador.
    const today = new Date().toISOString().slice(0, 10);
    const snapshot = unidades.find(u => u.unidad_id === unidadId);
    setUnidades(prev => prev.map(u => u.unidad_id === unidadId ? { ...u, servicio_busqueda_inquilino_activo: true, fecha_alta_busqueda: today } : u));
    try {
      await unidadesApi.update(unidadId, {
        servicio_busqueda_inquilino_activo: true,
        fecha_alta_busqueda: today,
      });
      toast.success('Búsqueda activada');
    } catch (err) {
      // Rollback
      setUnidades(prev => prev.map(u => u.unidad_id === unidadId ? { ...u, servicio_busqueda_inquilino_activo: !!snapshot?.servicio_busqueda_inquilino_activo, fecha_alta_busqueda: snapshot?.fecha_alta_busqueda } : u));
      toast.error('Error al activar búsqueda');
    }
  };

  const handleAltaAdminTipoChange = (tipo) => {
    const defaultPorcentaje = tipo === 'full' ? 20 : 8;
    setAltaAdminForm(prev => ({
      ...prev,
      tipo_administracion: tipo,
      porcentaje_administracion: defaultPorcentaje
    }));
  };

  const handleContratoAdminUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingContratoAdmin(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', 'contrato_administracion');
      
      const res = await archivosApi.upload(formData);
      setAltaAdminForm(prev => ({
        ...prev,
        contrato_file: {
          url: res.data.url,
          nombre: file.name,
          uploaded_at: new Date().toISOString()
        }
      }));
      toast.success('Contrato subido');
    } catch (err) {
      console.error('Error uploading contrato:', err);
      toast.error('Error al subir el contrato');
    } finally {
      setUploadingContratoAdmin(false);
    }
  };

  const handleSaveAltaAdmin = async (e) => {
    e.preventDefault();
    if (!altaAdminForm.unidad_id) {
      toast.error('Selecciona una unidad o grupo');
      return;
    }
    if (!altaAdminForm.servicio_busqueda_activo && !altaAdminForm.servicio_admin_activo) {
      toast.error('Marcá al menos un servicio (búsqueda o administración mensual)');
      return;
    }
    
    setSavingAltaAdmin(true);
    try {
      const buildPayload = (incluirContratoAdmin = true, incluirContratoBusqueda = true) => {
        const payload = {};
        if (altaAdminForm.servicio_admin_activo) {
          payload.en_administracion = true;
          payload.tipo_administracion = altaAdminForm.tipo_administracion;
          payload.porcentaje_administracion = altaAdminForm.porcentaje_administracion;
          payload.fecha_alta_administracion = new Date().toISOString().slice(0, 10);
          if (incluirContratoAdmin && altaAdminForm.contrato_file) {
            payload.contrato_administracion = altaAdminForm.contrato_file;
          }
        }
        if (altaAdminForm.servicio_busqueda_activo) {
          payload.servicio_busqueda_inquilino_activo = true;
          payload.fecha_alta_busqueda = new Date().toISOString().slice(0, 10);
          if (incluirContratoBusqueda && altaAdminForm.contrato_busqueda_file) {
            payload.contrato_busqueda_inquilino = altaAdminForm.contrato_busqueda_file;
          }
        }
        return payload;
      };

      const isGrupo = altaAdminForm.unidad_id.startsWith('grupo_');
      const grupoDeUnidad = isGrupo ? null : getGrupoDe(altaAdminForm.unidad_id);
      const expandeATodoGrupo = isGrupo || (grupoDeUnidad && altaAdminForm.alcance_grupo === 'todo_grupo');

      // ¿Reparte mismo contrato a todos los items del grupo?
      const compartirContratoAdmin = altaAdminForm.contrato_tipo === 'mismo';

      let unidadIds = [];
      if (isGrupo) {
        const grupoId = altaAdminForm.unidad_id.replace('grupo_', '');
        const grupo = grupos.find(g => g.grupo_id === grupoId);
        if (!grupo) { toast.error('Grupo no encontrado'); return; }
        unidadIds = grupo.unidad_ids;
      } else if (grupoDeUnidad && altaAdminForm.alcance_grupo === 'todo_grupo') {
        unidadIds = grupoDeUnidad.unidad_ids;
      } else {
        unidadIds = [altaAdminForm.unidad_id];
      }

      for (const unidadId of unidadIds) {
        const payload = buildPayload(
          !expandeATodoGrupo || compartirContratoAdmin,  // contrato admin compartido si grupo+mismo
          !expandeATodoGrupo || compartirContratoAdmin   // contrato búsqueda mismo criterio
        );
        await unidadesApi.update(unidadId, payload);
      }

      toast.success(
        unidadIds.length > 1
          ? `Servicios dados de alta para ${unidadIds.length} unidades`
          : 'Servicios dados de alta'
      );
      setIsAltaAdminOpen(false);
      fetchData(true); // refresco silencioso: no resetea el buscador de la pestaña
    } catch (err) {
      console.error('Error saving alta servicios:', err);
      toast.error(err.response?.data?.detail || 'Error al dar de alta');
    } finally {
      setSavingAltaAdmin(false);
    }
  };


  const handleBulkToggleAdmin = async (unidad_ids, en_administracion) => {
    if (!Array.isArray(unidad_ids) || unidad_ids.length === 0) return;
    // Optimistic update: aplicamos el cambio en local antes de pegarle al backend.
    // No mostramos toast en el éxito. Si falla, revertimos y avisamos del error.
    const idSet = new Set(unidad_ids);
    const snapshot = unidades
      .filter(u => idSet.has(u.unidad_id))
      .map(u => ({ unidad_id: u.unidad_id, en_administracion: u.en_administracion }));
    setUnidades(prev => prev.map(u => idSet.has(u.unidad_id) ? { ...u, en_administracion } : u));
    try {
      await unidadesApi.bulkToggleAdmin(unidad_ids, en_administracion);
    } catch (err) {
      // Revertir
      const prevById = Object.fromEntries(snapshot.map(s => [s.unidad_id, s.en_administracion]));
      setUnidades(prev => prev.map(u => idSet.has(u.unidad_id) ? { ...u, en_administracion: prevById[u.unidad_id] } : u));
      toast.error('Error al actualizar administración');
    }
  };

  const handleBajaAdmin = async () => {
    if (!bajaAdminTarget) return;
    
    try {
      await unidadesApi.update(bajaAdminTarget, {
        en_administracion: false,
        tipo_administracion: null,
        porcentaje_administracion: null,
        contrato_administracion: null
      });
      toast.success('Unidad dada de baja de administración');
      setBajaAdminTarget(null);
      fetchData(true); // refresco silencioso: no resetea el buscador de la pestaña
    } catch (err) {
      toast.error('Error al dar de baja');
    }
  };

  const handleUploadContratoAdmin = async (unidadId, file) => {
    if (!file) return;
    try {
      const res = await unidadesApi.uploadContratoAdministracion(unidadId, file);
      // Backend returns {ok, contrato_administracion: {filename, ...}}. Mapeamos al campo que la UI lee.
      const archivo = res?.data?.contrato_administracion || null;
      if (archivo) {
        setUnidades(prev => prev.map(u => u.unidad_id === unidadId
          ? { ...u, contrato_administracion_archivo: archivo }
          : u));
      }
      toast.success('Contrato subido');
    } catch (err) {
      console.error('Error uploading contrato:', err);
      toast.error(err.response?.data?.detail || 'Error al subir el contrato');
    }
  };

  const handleDeleteContratoAdmin = async (unidadId) => {
    try {
      await unidadesApi.deleteContratoAdministracion(unidadId);
      // Surgical update: clear file ref on that row only
      setUnidades(prev => prev.map(u => u.unidad_id === unidadId
        ? { ...u, contrato_administracion_archivo: null }
        : u));
      toast.success('Contrato eliminado');
    } catch (err) {
      console.error('Error deleting contrato:', err);
      toast.error(err.response?.data?.detail || 'Error al eliminar el contrato');
    }
  };

  const handleAltaRapidaAddRow = () => {
    setAltaRapidaData([...altaRapidaData, { nombre_unidad: '', tipo: 'oficina' }]);
  };

  const handleAltaRapidaRemoveRow = (index) => {
    if (altaRapidaData.length === 1) return;
    setAltaRapidaData(altaRapidaData.filter((_, i) => i !== index));
  };

  const handleAltaRapidaChange = (index, field, value) => {
    const newData = [...altaRapidaData];
    newData[index][field] = value;
    setAltaRapidaData(newData);
  };

  const handleSaveAltaRapida = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const unidadesValidas = altaRapidaData.filter(u => u.nombre_unidad.trim());
      if (unidadesValidas.length === 0) {
        toast.error('Ingresa al menos una unidad');
        setSaving(false);
        return;
      }
      await unidadesApi.createAltaRapida({
        edificio_id: edificioId,
        unidades: unidadesValidas.map(u => ({
          nombre_unidad: u.nombre_unidad.trim(),
          m2_cubiertos: 0,
          m2_semicubiertos: 0,
          tipo: u.tipo,
        })),
      });
      toast.success(`${unidadesValidas.length} unidades creadas`);
      setIsAltaRapidaDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState message="Cargando edificio..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;
  if (!edificio) return <ErrorState message="Edificio no encontrado" />;

  const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';

  return (
    <div className="w-full edif-fit">
      {/* STICKY SECTION: Header + Tabs + Controls */}
      <div ref={stickyHeaderRef} className="sticky top-0 z-30 bg-white">
        {/* Header compacto */}
        {/* Header: nombre del edificio + tabs en la misma fila */}
        <div className="px-4 md:px-6 pt-3 pb-2 bg-white border-b border-slate-100">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg pl-4 pr-2 py-1.5">
              <div className="flex items-center gap-2 flex-none">
                <h1 className="text-lg font-bold text-slate-900 whitespace-nowrap">{edificio.nombre}</h1>
                <StatusBadge status={edificio.estado} />
                {!isComercial && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditEdificio}
                    data-testid="edit-edificio-btn"
                    className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                    title="Editar edificio"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <div className="h-5 w-px bg-slate-200 flex-none"></div>
              <TabsList className="flex-1 min-w-0 justify-start overflow-x-auto overflow-y-hidden flex-nowrap [&>*]:flex-none bg-transparent p-0 h-auto">
              {!isComercial && (
                <TabsTrigger value="config" data-testid="tab-config" className="text-xs">
                  1. Unidades
                </TabsTrigger>
              )}
              <TabsTrigger value="administracion" data-testid="tab-administracion" className="text-xs">
                {isComercial ? 'Admin' : '2. Admin'}
              </TabsTrigger>
              <TabsTrigger value="contratos" data-testid="tab-contratos" className="text-xs">
                {isComercial ? 'Contratos' : '3. Contratos'}
              </TabsTrigger>
              {!isComercial && edificio?.modelo_administracion === 'propietario_factura_directo' && (
                <TabsTrigger value="inquilinos" data-testid="tab-inquilinos" className="text-xs">
                  4. Inquilinos
                </TabsTrigger>
              )}
              <TabsTrigger value="historicos" data-testid="tab-historicos" className="text-xs">
                {isComercial
                  ? 'Historial'
                  : edificio?.modelo_administracion === 'propietario_factura_directo'
                    ? '5. Historial'
                    : '4. Historial'}
              </TabsTrigger>
              {!isComercial && edificio?.modelo_administracion === 'propietario_factura_directo' ? (
                <>
                  <TabsTrigger value="factura" data-testid="tab-factura" className="text-xs">6. Facturación</TabsTrigger>
                  <TabsTrigger value="cobros" data-testid="tab-cobros" className="text-xs">7. Cobros</TabsTrigger>
                  <TabsTrigger value="morosos" data-testid="tab-morosos" className="text-xs">8. Morosos</TabsTrigger>
                  <TabsTrigger value="egresos" data-testid="tab-egresos" className="text-xs">9. Control mensual</TabsTrigger>
                  {unidades?.some(u => u.tipo === 'cowork') && (
                    <TabsTrigger value="cowork" data-testid="tab-cowork" className="text-xs">10. Cowork</TabsTrigger>
                  )}
                </>
              ) : (
                !isComercial && <></>
              )}
              {!isComercial && edificio?.modelo_administracion !== 'propietario_factura_directo' && (
                <>
                  <TabsTrigger value="factura" data-testid="tab-factura" className="text-xs">5. Factura</TabsTrigger>
                  <TabsTrigger value="cobro" data-testid="tab-cobro" className="text-xs">6. Cobro</TabsTrigger>
                  <TabsTrigger value="morosos" data-testid="tab-morosos" className="relative text-xs">
                    7. Morosos
                    {indicadoresFacturacion?.meses_anteriores?.falta_cobrar > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full">
                        {indicadoresFacturacion.meses_anteriores.falta_cobrar}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="egresos" data-testid="tab-egresos" className="text-xs">8. Egresos</TabsTrigger>
                  <TabsTrigger value="carga" data-testid="tab-carga" className="text-xs">9. Liquid.</TabsTrigger>
                  <TabsTrigger value="pagos" data-testid="tab-pagos" className="text-xs">10. Pagos</TabsTrigger>
                  {isAnalista && (
                    <TabsTrigger value="vista-inversor" data-testid="tab-vista-inversor" className="text-xs">11. Vista Inversor</TabsTrigger>
                  )}
                </>
              )}
            </TabsList>
            </div>
          </Tabs>
        </div>

        {/* Controles de Facturación - dentro del sticky (solo se renderiza en la tab facturacion) */}
        {activeTab === 'facturacion' && (
        <div className="px-4 md:px-6 pb-2 bg-white border-b border-slate-100">
          {activeTab === 'facturacion' && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <div className="flex items-center">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="w-[120px] mx-2">
                  <PeriodSelector
                    value={selectedPeriodo}
                    onChange={setSelectedPeriodo}
                    placeholder="Período"
                  />
                </div>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                
                <Select value={filtroEstadoFactura} onValueChange={setFiltroEstadoFactura}>
                  <SelectTrigger className="h-8 w-[130px] text-xs ml-8">
                    <SelectValue placeholder="Filtrar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="creada">Creada</SelectItem>
                    <SelectItem value="enviada">Enviada</SelectItem>
                    <SelectItem value="cobro_parcial">Cobro Parcial</SelectItem>
                    <SelectItem value="cobrada">Cobrada</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Leyenda semáforo */}
                <div className="flex items-center gap-3 ml-6 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    hasta día 10
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                    día 11-15
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    día 16+
                  </span>
                </div>
                
                {loadingFacturacion && (
                  <span className="text-xs text-blue-600 ml-2 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                  </span>
                )}
              </div>
              
              {/* Indicadores de facturación */}
              {indicadoresFacturacion && (
                <div className="flex items-center gap-6 text-xs">
                  {/* Mes actual */}
                  <div className="flex items-center gap-3 bg-slate-50 px-3 py-1.5 rounded-lg">
                    <span className="text-slate-500 font-medium">Mes actual:</span>
                    <div className="flex items-center gap-1">
                      <span className={cn(
                        "font-bold",
                        indicadoresFacturacion.mes_actual.falta_facturar > 0 ? "text-amber-600" : "text-green-600"
                      )}>
                        {indicadoresFacturacion.mes_actual.falta_facturar}
                      </span>
                      <span className="text-slate-500">falta facturar</span>
                    </div>
                    <div className="h-3 w-px bg-slate-300"></div>
                    <div className="flex items-center gap-1">
                      <span className={cn(
                        "font-bold",
                        indicadoresFacturacion.mes_actual.falta_cobrar > 0 ? "text-red-600" : "text-green-600"
                      )}>
                        {indicadoresFacturacion.mes_actual.falta_cobrar}
                      </span>
                      <span className="text-slate-500">falta cobrar</span>
                    </div>
                  </div>
                  
                  {/* Meses anteriores */}
                  {(indicadoresFacturacion.meses_anteriores.falta_facturar > 0 || indicadoresFacturacion.meses_anteriores.falta_cobrar > 0) && (
                    <div className="flex items-center gap-3 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
                      <span className="text-red-700 font-medium">Meses anteriores:</span>
                      {indicadoresFacturacion.meses_anteriores.falta_facturar > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-red-700">
                            {indicadoresFacturacion.meses_anteriores.falta_facturar}
                          </span>
                          <span className="text-red-600">sin facturar</span>
                        </div>
                      )}
                      {indicadoresFacturacion.meses_anteriores.falta_facturar > 0 && indicadoresFacturacion.meses_anteriores.falta_cobrar > 0 && (
                        <div className="h-3 w-px bg-red-300"></div>
                      )}
                      {indicadoresFacturacion.meses_anteriores.falta_cobrar > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-red-700">
                            {indicadoresFacturacion.meses_anteriores.falta_cobrar}
                          </span>
                          <span className="text-red-600">sin cobrar</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Encabezados tabla Facturación - dentro del sticky */}
          {activeTab === 'facturacion' && facturacionData && facturacionData.items.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 border-b-0 rounded-t-lg overflow-x-auto">
              <table className="w-full text-xs font-semibold text-slate-600 table-fixed">
                <thead>
                  <tr>
                    <th className="py-2 px-3 text-left">Unidad</th>
                    <th className="py-2 px-3 text-left">Inquilino</th>
                    <th className="py-2 px-3 text-right">Anterior Neto</th>
                    <th className="py-2 px-3 text-right">Monto Neto</th>
                    <th className="py-2 px-3 text-right">IVA</th>
                    <th className="py-2 px-3 text-right">Total</th>
                    <th className="py-2 px-3 text-center">Revisión</th>
                    <th className="py-2 px-3 text-center">Fecha Rev.</th>
                  </tr>
                </thead>
              </table>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Content area - ocupa el alto restante del viewport (sin franja blanca) */}
      <div
        className="px-4 md:px-6 flex flex-col overflow-hidden"
        style={{ height: stickyH ? `calc(100vh - ${stickyH}px)` : undefined }}
      >
      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="modulo-fill flex flex-col flex-1 min-h-0">
        {/* Controls for each tab */}
        <div className={cn(activeTab === 'carga' && unidades.length > 0 ? 'mb-3' : '')}>
          {/* Period selector and save button - only for Carga Mensual */}
          {activeTab === 'carga' && unidades.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="w-[140px]">
                  <PeriodSelector
                    value={selectedPeriodo}
                    onChange={setSelectedPeriodo}
                    placeholder="Período"
                  />
                </div>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <RentaEmailConfigButton />
                {cargandoAlquileres && (
                  <span className="text-xs text-blue-600 ml-2 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Calculando alquileres...
                  </span>
                )}
              </div>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="totales-superior">
                <div className="flex items-baseline gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Ingresos</span>
                  <span className="font-mono font-semibold text-emerald-700">$ {formatImporte(totalesLiquidacion.ingreso, 0)}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Egresos</span>
                  <span className="font-mono font-semibold text-rose-700">$ {formatImporte(totalesLiquidacion.egreso, 0)}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Fee</span>
                  <span className="font-mono font-semibold text-amber-700">$ {formatImporte(totalesLiquidacion.fee, 0)}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Ganancia</span>
                  <span className={cn("font-mono font-semibold", totalesLiquidacion.ganancia >= 0 ? "text-blue-700" : "text-rose-700")}>$ {formatImporte(totalesLiquidacion.ganancia, 0)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* TAB: Facturación/Cobro */}
        {!isComercial && <>
        {edificio?.edificio_id === edificioId && edificio?.modelo_administracion === 'propietario_factura_directo' ? (
          <>
            <TabAlquilerMensualForesta edificioId={edificioId} periodo={selectedPeriodo} setSelectedPeriodo={setSelectedPeriodo} activeTab={activeTab} />
            <TabCobrosForesta edificioId={edificioId} periodo={selectedPeriodo} setSelectedPeriodo={setSelectedPeriodo} activeTab={activeTab} />
            <TabMorososForesta edificioId={edificioId} periodo={selectedPeriodo} setSelectedPeriodo={setSelectedPeriodo} activeTab={activeTab} />
            <TabControlMensualForesta edificioId={edificioId} periodo={selectedPeriodo} setSelectedPeriodo={setSelectedPeriodo} activeTab={activeTab} />
            {unidades?.some(u => u.tipo === 'cowork') && (
              <TabsContent value="cowork" className="mt-0" data-testid="tab-content-cowork">
                <CoworkForesta edificioId={edificioId} />
              </TabsContent>
            )}
          </>
        ) : (
          <>
        <TabFactura
          cn={cn}
          edificioId={edificioId}
          error={error}
          expandedGrupos={expandedGrupos}
          facturacionData={facturacionData}
          facturasCreadas={facturasCreadas}
          filtroEstadoFactura={filtroEstadoFactura}
          formatImporte={formatImporte}
          getGroupedFacItems={getGroupedFacItems}
          loading={loading}
          loadingFacturacion={loadingFacturacion}
          navigatePeriodo={navigatePeriodo}
          selectedPeriodo={selectedPeriodo}
          setFacturacionData={setFacturacionData}
          setFacturasCreadas={setFacturasCreadas}
          setSelectedPeriodo={setSelectedPeriodo}
          toast={toast}
          toggleGrupoExpanded={toggleGrupoExpanded}
          unidades={unidades}
          contratos={contratos}
        />

        {/* TAB: Cobro */}
        <TabCobro
          getGroupedFacItems={getGroupedFacItems}
          expandedGrupos={expandedGrupos}
          toggleGrupoExpanded={toggleGrupoExpanded}
          cn={cn}
          edificio={edificio}
          edificioId={edificioId}
          error={error}
          facturacionData={facturacionData}
          formatImporte={formatImporte}
          handleSyncColppy={handleSyncColppy}
          loadingFacturacion={loadingFacturacion}
          navigatePeriodo={navigatePeriodo}
          selectedPeriodo={selectedPeriodo}
          setFacturacionData={setFacturacionData}
          setSelectedPeriodo={setSelectedPeriodo}
          syncingColppy={syncingColppy}
          toast={toast}
          unidades={unidades}
        />

        {/* TAB: Morosos */}
        <TabMorosos
          cn={cn}
          edificioId={edificioId}
          error={error}
          formatImporte={formatImporte}
          loadingMorosos={loadingMorosos}
          morososData={morososData}
          selectedPeriodo={selectedPeriodo}
          setFacturacionData={setFacturacionData}
          setIndicadoresFacturacion={setIndicadoresFacturacion}
          setMorososData={setMorososData}
          toast={toast}
        />

        {/* TAB: Egresos */}
        <TabEgresos
          cn={cn}
          contratos={contratos}
          contratoBadgeByUnidad={contratoBadgeByUnidad}
          edificio={edificio}
          edificioId={edificioId}
          egresosData={egresosData}
          egresosHasChanges={egresosHasChanges}
          error={error}
          expandedGrupos={expandedGrupos}
          facturacionData={facturacionData}
          formatImporte={formatImporte}
          getGroupedOrder={getGroupedOrder}

          navigatePeriodo={navigatePeriodo}
          saving={saving}
          savingEgresos={savingEgresos}
          selectedPeriodo={selectedPeriodo}
          setEgresosData={setEgresosData}
          setEgresosHasChanges={setEgresosHasChanges}
          setSavingEgresos={setSavingEgresos}
          setSelectedPeriodo={setSelectedPeriodo}
          sortUnidades={sortUnidades}
          toast={toast}
          toggleGrupoExpanded={toggleGrupoExpanded}
          unidades={unidades}
        />

        {/* TAB: Carga Mensual */}
        <TabLiquidacion
          edificioId={edificioId}
          onAvisoEnviado={(uids, at) => setTableData(prev => {
            const n = { ...prev };
            uids.forEach(u => { if (n[u]) n[u] = { ...n[u], renta_email_enviada_at: at, renta_email_error: null }; });
            return n;
          })}
          calculateGanancia={calculateGanancia}
          cn={cn}
          contratoBadgeByUnidad={contratoBadgeByUnidad}
          cocherasAsociadasMap={cocherasAsociadasMap}
          cocherasDetalleByMadre={cocherasDetalleByMadre}
          cotizacionPeriodo={cotizacionPeriodo}
          edificio={edificio}
          expandedGrupos={expandedGrupos}
          formatImporte={formatImporte}
          getGroupedOrder={getGroupedOrder}
          handleFileUpload={handleFileUpload}
          handlePublicar={handlePublicar}
          handleCambiarEstado={handleCambiarEstado}
          handleCambiarEstadoGrupo={handleCambiarEstadoGrupo}
          handleTabChange={handleTabChange}
          handleTableChange={handleTableChange}
          getDownloadUrl={(resultadoId, tipo) => archivosApi.downloadResultado(resultadoId, tipo)}
          sortUnidades={sortUnidades}
          tableData={tableData}
          toggleGrupoExpanded={toggleGrupoExpanded}
          totales={totalesLiquidacion}
          unidades={unidades}
          uploadingFile={uploadingFile}
        />

        {/* TAB: Pagos */}
        <TabPagos
          cn={cn}
          contratoBadgeByUnidad={contratoBadgeByUnidad}
          cocherasAsociadasMap={cocherasAsociadasMap}
          edificio={edificio}
          edificioId={edificioId}
          expandedGrupos={expandedGrupos}
          formatImporte={formatImporte}
          getGroupedOrder={getGroupedOrder}
          navigatePeriodo={navigatePeriodo}
          selectedPeriodo={selectedPeriodo}
          setSelectedPeriodo={setSelectedPeriodo}
          sortUnidades={sortUnidades}
          toast={toast}
          toggleGrupoExpanded={toggleGrupoExpanded}
          unidades={unidades}
        />

        {/* TAB: Vista Inversor (solo analista) */}
        {isAnalista && (
          <TabsContent value="vista-inversor" className="mt-0" data-testid="tab-content-vista-inversor">
            <TabVistaInversor edificioId={edificioId} />
          </TabsContent>
        )}
          </>
        )}

        {/* TAB: Configuración Unidades */}
        <TabUnidades
          busquedaOficina={busquedaOficina}
          edificio={edificio}
          expandedGrupos={expandedGrupos}
          filtroTipo={filtroTipo}
          grupos={grupos}
          handleOpenAltaRapida={handleOpenAltaRapida}
          handleOpenEditGrupo={handleOpenEditGrupo}
          handleOpenEditUnidad={handleOpenEditUnidad}
          handleOpenGrupo={handleOpenGrupo}
          handleOpenPropietario={handleOpenPropietario}
          handleRemovePropietario={handleRemovePropietario}
          setBusquedaOficina={setBusquedaOficina}
          setDeleteGrupoTarget={setDeleteGrupoTarget}
          setFiltroTipo={setFiltroTipo}
          setIsDeleteUnidadOpen={setIsDeleteUnidadOpen}
          setSelectedUnidad={setSelectedUnidad}
          sortUnidades={sortUnidades}
          toggleGrupoExpanded={toggleGrupoExpanded}
          unidades={unidades}
        />
        </>}

        {/* TAB: Contratos Alquiler */}
        <TabsContent value="contratos" className="mt-0">
          <ContratosDepositos 
            edificioId={edificioId} 
            edificio={edificio}
            unidades={unidades}
            mode="contratos" 
            onReloadUnidades={fetchData}
          />
        </TabsContent>

        {/* TAB: Inquilinos (solo Foresta) */}
        {edificio?.modelo_administracion === 'propietario_factura_directo' && (
          <TabInquilinos edificioId={edificioId} activeTab={activeTab} />
        )}

        {/* TAB: Históricos */}
        <TabHistoricos
          contratos={contratos}
          expandedHistoricos={expandedHistoricos}
          historicosData={historicosData}
          loadingHistoricos={loadingHistoricos}
          propietarios={propietarios}
          setExpandedHistoricos={setExpandedHistoricos}
          onHistorialDeleted={(unidadId) => {
            // Surgical update: filtrar la unidad del listado (no full reload).
            setHistoricosData(prev => prev.filter(item => item.unidad_id !== unidadId));
          }}
        />

        {/* TAB: Administración → ahora "Servicios Contratados" */}
        <TabAdministracion
          cn={cn}
          edificio={edificio}
          edificioId={edificioId}
          handleOpenAltaAdmin={handleOpenAltaAdmin}
          handleUploadContratoAdmin={handleUploadContratoAdmin}
          handleDeleteContratoAdmin={handleDeleteContratoAdmin}
          handleUploadContratoBusqueda={handleUploadContratoBusqueda}
          handleBajaServicioBusqueda={handleBajaServicioBusqueda}
          handleActivarServicioBusqueda={handleActivarServicioBusqueda}
          setBajaAdminTarget={setBajaAdminTarget}
          onBulkToggleAdmin={handleBulkToggleAdmin}
          onEditAdmin={(unidad) => setEditAdminUnidad(unidad)}
          sortUnidades={sortUnidades}
          unidades={unidades}
        />

      </Tabs>

      {/* ============ DIALOGS ============ */}

      {/* Edit Edificio Dialog */}
        <EditEdificioDialog
          contratos={contratos}
          edificio={edificio}
          edificioForm={edificioForm}
          handleSaveEdificio={handleSaveEdificio}
          isEditEdificioOpen={isEditEdificioOpen}
          saving={saving}
          setEdificioForm={setEdificioForm}
          setIsEditEdificioOpen={setIsEditEdificioOpen}
          onEdificioUpdated={fetchData}
        />

      {/* Unidad Dialog - Solo datos básicos */}
        <UnidadDialog
          edificioId={edificioId}
          handleSaveUnidad={handleSaveUnidad}
          isUnidadDialogOpen={isUnidadDialogOpen}
          saving={saving}
          selectedUnidad={selectedUnidad}
          setIsUnidadDialogOpen={setIsUnidadDialogOpen}
          setUnidadForm={setUnidadForm}
          setUnidadTab={setUnidadTab}
          unidadForm={unidadForm}
          unidadTab={unidadTab}
          onOpenCartaOferta={(u) => { setSelectedUnidadCarta(u); setIsCartaOfertaDialogOpen(true); }}
          onOpenPosesion={(u) => { setSelectedUnidadPosesion(u); setIsPosesionDialogOpen(true); }}
          onOpenEscritura={(u) => { setSelectedUnidadEscritura(u); setEscrituraFile(null); setEscrituraAnalisis(u.escritura_analisis || null); setIsEscrituraDialogOpen(true); }}
          escrituraGeneralEdificio={(edificio?.documentacion || {}).escritura_general}
          onDownloadEscrituraGeneral={() => {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/edificios/${edificioId}/escritura-general/download`;
            const a = document.createElement('a');
            a.href = url;
            const token = localStorage.getItem('token');
            // El endpoint requiere auth, abrimos fetch+blob para forzar header
            fetch(url, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => { if (!r.ok) throw new Error('descarga fallida'); return r.blob(); })
              .then(blob => {
                const objUrl = URL.createObjectURL(blob);
                a.href = objUrl;
                a.download = (edificio?.documentacion?.escritura_general?.filename) || 'escritura.pdf';
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
              })
              .catch(() => toast.error('No se pudo descargar la escritura'));
          }}
          onDeleteUnidad={(u) => { setSelectedUnidad(u); setIsUnidadDialogOpen(false); setIsDeleteUnidadOpen(true); }}
        />

      {/* Propietario Dialog - Soporta múltiples propietarios con porcentaje */}
        <PropietarioDialog
          cn={cn}
          editingPropietario={editingPropietario}
          handleSavePropietario={handleSavePropietario}
          isPropietarioDialogOpen={isPropietarioDialogOpen}
          propietarioForm={propietarioForm}
          propietarioSearch={propietarioSearch}
          propietarios={propietarios}
          resultados={resultados}
          saving={saving}
          selectedUnidad={selectedUnidad}
          setEditingPropietario={setEditingPropietario}
          setIsNuevoPropietarioOpen={setIsNuevoPropietarioOpen}
          setIsPropietarioDialogOpen={setIsPropietarioDialogOpen}
          setPropietarioForm={setPropietarioForm}
          setPropietarioSearch={setPropietarioSearch}
        />

      {/* Escritura Dialog - Cargar/Ver escritura con IA */}
        <EscrituraDialog
          analizandoEscritura={analizandoEscritura}
          error={error}
          escrituraAnalisis={escrituraAnalisis}
          escrituraFile={escrituraFile}
          fetchData={fetchData}
          isEscrituraDialogOpen={isEscrituraDialogOpen}
          propietarios={propietarios}
          selectedUnidadEscritura={selectedUnidadEscritura}
          setAnalizandoEscritura={setAnalizandoEscritura}
          setEscrituraAnalisis={setEscrituraAnalisis}
          setEscrituraFile={setEscrituraFile}
          setIsEscrituraDialogOpen={setIsEscrituraDialogOpen}
          setSelectedUnidadEscritura={setSelectedUnidadEscritura}
          toast={toast}
        />

      {/* Carta Oferta Dialog (independiente de la escritura) */}
        <CartaOfertaDialog
          isOpen={isCartaOfertaDialogOpen}
          setIsOpen={setIsCartaOfertaDialogOpen}
          selectedUnidad={selectedUnidadCarta}
          onReload={fetchData}
          toast={toast}
        />

      {/* Posesión Dialog (independiente de la escritura y la carta oferta) */}
        <PosesionDialog
          isOpen={isPosesionDialogOpen}
          setIsOpen={setIsPosesionDialogOpen}
          selectedUnidad={selectedUnidadPosesion}
          onReload={fetchData}
          toast={toast}
        />

      {/* Nuevo Propietario Dialog - Formulario completo con pestañas */}
        <NuevoPropietarioDialog
          handleCreateNuevoPropietario={handleCreateNuevoPropietario}
          isNuevoPropietarioOpen={isNuevoPropietarioOpen}
          nuevoPropietarioForm={nuevoPropietarioForm}
          nuevoPropietarioTab={nuevoPropietarioTab}
          saving={saving}
          setIsNuevoPropietarioOpen={setIsNuevoPropietarioOpen}
          setNuevoPropietarioForm={setNuevoPropietarioForm}
          setNuevoPropietarioTab={setNuevoPropietarioTab}
          setShowNuevoPropPassword={setShowNuevoPropPassword}
          showNuevoPropPassword={showNuevoPropPassword}
        />

      {/* Masivo Dialog */}
        <MasivoDialog
          handleSaveMasivo={handleSaveMasivo}
          isMasivoDialogOpen={isMasivoDialogOpen}
          masivoData={masivoData}
          propietarios={propietarios}
          saving={saving}
          setIsMasivoDialogOpen={setIsMasivoDialogOpen}
          setMasivoData={setMasivoData}
        />

      {/* Alta de Unidades Dialog */}
        <AltaRapidaDialog
          altaRapidaData={altaRapidaData}
          handleAltaRapidaAddRow={handleAltaRapidaAddRow}
          handleAltaRapidaChange={handleAltaRapidaChange}
          handleAltaRapidaRemoveRow={handleAltaRapidaRemoveRow}
          handleSaveAltaRapida={handleSaveAltaRapida}
          isAltaRapidaDialogOpen={isAltaRapidaDialogOpen}
          saving={saving}
          setIsAltaRapidaDialogOpen={setIsAltaRapidaDialogOpen}
          unidades={unidades}
        />

      {/* Delete Unidad */}
      <AlertDialog open={isDeleteUnidadOpen} onOpenChange={setIsDeleteUnidadOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar unidad?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{selectedUnidad?.nombre_unidad}" y todos sus resultados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUnidad} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Agrupar Unidades Dialog */}
        <GrupoDialog
          cn={cn}
          editingGrupoId={editingGrupoId}
          getGrupoDe={getGrupoDe}
          grupoForm={grupoForm}
          grupos={grupos}
          handleSaveGrupo={handleSaveGrupo}
          isGrupoDialogOpen={isGrupoDialogOpen}
          savingGrupo={savingGrupo}
          setEditingGrupoId={setEditingGrupoId}
          setGrupoForm={setGrupoForm}
          setIsGrupoDialogOpen={setIsGrupoDialogOpen}
          sortUnidades={sortUnidades}
          unidades={unidades}
        />

      {/* Alta de Servicios Dialog */}
        <AltaAdminDialog
          altaAdminForm={altaAdminForm}
          getGrupoDe={getGrupoDe}
          grupos={grupos}
          handleAltaAdminTipoChange={handleAltaAdminTipoChange}
          handleContratoAdminUpload={handleContratoAdminUpload}
          handleContratoBusquedaUpload={handleContratoBusquedaUpload}
          handleSaveAltaAdmin={handleSaveAltaAdmin}
          isAltaAdminOpen={isAltaAdminOpen}
          savingAltaAdmin={savingAltaAdmin}
          setAltaAdminForm={setAltaAdminForm}
          setIsAltaAdminOpen={setIsAltaAdminOpen}
          sortUnidades={sortUnidades}
          unidades={unidades}
          unidadesEnGrupo={unidadesEnGrupo}
          uploadingContratoAdmin={uploadingContratoAdmin}
          uploadingContratoBusqueda={uploadingContratoBusqueda}
        />

      {/* Editar Administración Dialog */}
        <EditAdminDialog
          open={!!editAdminUnidad}
          onOpenChange={(o) => { if (!o) setEditAdminUnidad(null); }}
          unidad={editAdminUnidad}
          onSaved={() => { setEditAdminUnidad(null); fetchData(true); }}
        />

      {/* Modal Cambiar Propietario */}
        <BajaAdminDialog
          cambiarPropietarioForm={cambiarPropietarioForm}
          cn={cn}
          handleSaveCambiarPropietario={handleSaveCambiarPropietario}
          isCambiarPropietarioOpen={isCambiarPropietarioOpen}
          propietarioSearch={propietarioSearch}
          propietarios={propietarios}
          saving={saving}
          selectedUnidad={selectedUnidad}
          setCambiarPropietarioForm={setCambiarPropietarioForm}
          setIsCambiarPropietarioOpen={setIsCambiarPropietarioOpen}
          setPropietarioSearch={setPropietarioSearch}
        />

      {/* Delete Grupo Confirmation */}
      <AlertDialog open={!!deleteGrupoTarget} onOpenChange={(open) => !open && setDeleteGrupoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desagrupar unidades?</AlertDialogTitle>
            <AlertDialogDescription>
              Las unidades volverán a aparecer de forma individual en la lista.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGrupo} className="bg-red-600 hover:bg-red-700">
              Desagrupar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Baja Admin Confirmation */}
      <AlertDialog open={!!bajaAdminTarget} onOpenChange={(open) => !open && setBajaAdminTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Dar de baja de administración?</AlertDialogTitle>
            <AlertDialogDescription>
              La unidad dejará de estar en administración.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleBajaAdmin} className="bg-red-600 hover:bg-red-700">
              Dar de Baja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default AnalistaEdificioDetalle;
